import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { serviceApi } from '../lib/api';
import { readFileAsDataUrl, resizeToJpeg } from '../lib/images';
import AllSitesLink from '../components/AllSitesLink';
import StaffSignIn from '../components/StaffSignIn';
import useStaffSession from '../hooks/useStaffSession';
import { parseHash } from '../lib/sites';

// Service desk — service.silverservice.com. Technicians and the office: the
// jobs (repairs, maintenance, installs), the machines at each customer, and
// the service requests customers send from the ordering portal. Replaces
// MaintainX; see docs/site-map.md. Built phone-first — technicians use it
// on site.
//
// The office works requests through three tabs: Requested (no date yet),
// Scheduled / in progress, Completed. The job decides which tab it's on
// (serviceDesk.workflowStage on the server): giving a request a date moves it.
export default function Service() {
  const session = useStaffSession('service');
  const { token } = session;
  const [me, setMe] = useState(null);
  const [error, setError] = useState('');
  const [view, setView] = useState('');
  const [jobId, setJobId] = useState(null);
  const [assetId, setAssetId] = useState(null);
  const [newJob, setNewJob] = useState(null);       // null, or the prefill for a new job
  const [newAsset, setNewAsset] = useState(null);   // null, or the prefill for a new machine
  const [staff, setStaff] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({ requested: 0, scheduled: 0, website: 0 });
  const [refreshKey, setRefreshKey] = useState(0);
  const [parts, setParts] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const reloadLists = useCallback(() => Promise.all([serviceApi.parts(), serviceApi.checklists()])
    .then(([p, c]) => { setParts(p.parts); setChecklists(c.checklists); }).catch(() => {}), []);

  const refreshCounts = useCallback(() => Promise.all([serviceApi.jobs(), serviceApi.requests()])
    .then(([j, r]) => setCounts({
      requested: j.jobs.filter((x) => x.stage === 'requested').length,
      scheduled: j.jobs.filter((x) => x.stage === 'scheduled').length,
      website: r.requests.filter((x) => !x.jobId).length,
    })).catch(() => {}), []);

  useEffect(() => {
    if (!token) return;
    Promise.all([serviceApi.me(), serviceApi.staff(), serviceApi.customers()])
      .then(([m, s, c]) => {
        setMe(m);
        setStaff(s.staff);
        setCustomers(c.customers);
        setView((v) => v || (m.role === 'technician' ? 'day' : 'requested'));
      })
      .catch((e) => setError(e.message));
    reloadLists();
    // The office's emails link straight to a job: #service?job=…
    const linked = parseHash().params.get('job');
    if (linked) setJobId(linked);
  }, [token, reloadLists]);
  useEffect(() => { if (token) refreshCounts(); }, [token, refreshKey, refreshCounts]);

  const openJob = (id) => { setNewJob(null); setAssetId(null); setJobId(id); };
  const openAsset = (id) => { setJobId(null); setNewAsset(null); setAssetId(id); };
  const back = () => {
    if (parseHash().params.get('job')) window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#service`);
    setJobId(null); setAssetId(null); setNewJob(null); setNewAsset(null); setRefreshKey((k) => k + 1);
  };

  if (!token) return <StaffSignIn session={session} siteName="Service desk" />;

  const ctx = { me, staff, customers, parts, checklists, reloadLists, refreshCounts, openJob, openAsset, setNewJob, setNewAsset, back, refreshKey };
  let body;
  if (newJob) body = <RequestForm ctx={ctx} prefill={newJob} />;
  else if (newAsset) body = <AssetForm ctx={ctx} prefill={newAsset} />;
  else if (jobId) body = <JobDetail ctx={ctx} jobId={jobId} />;
  else if (assetId) body = <AssetDetail ctx={ctx} assetId={assetId} />;
  else if (view === 'day') body = <MyDay ctx={ctx} />;
  else if (view === 'requested') body = <RequestedBoard ctx={ctx} />;
  else if (view === 'scheduled') body = <ScheduledBoard ctx={ctx} />;
  else if (view === 'completed') body = <CompletedBoard ctx={ctx} />;
  else if (view === 'tracker') body = <Tracker ctx={ctx} />;
  else if (view === 'equipment') body = <Equipment ctx={ctx} />;
  else if (view === 'schedules') body = <Schedules ctx={ctx} />;
  else if (view === 'parts') body = <Parts ctx={ctx} />;
  else if (view === 'checklists') body = <Checklists ctx={ctx} />;
  else if (view === 'trends') body = <Trends ctx={ctx} />;

  const counted = (label, n) => `${label}${n ? ` (${n})` : ''}`;
  const tabs = [
    ['day', 'My day'], ['requested', counted('Requested', counts.requested + counts.website)], ['scheduled', counted('Scheduled / in progress', counts.scheduled)], ['completed', 'Completed'],
    ['tracker', 'Tracker'], ['schedules', 'Schedules'], ['equipment', 'Equipment'], ['parts', 'Parts'], ['checklists', 'Checklists'], ['trends', 'Trends'],
  ];
  return (
    <div style={s.page}>
      <div style={s.topBar}>
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <img src="/logo.png" alt="Silver Service" style={s.logoImg} />
          <span style={s.brandSmSub}>Service desk</span>
        </div>
        <div style={s.topRight}>
          <span style={s.who}>{me?.name}</span>
          <AllSitesLink style={s.linkBtn} />
          <button style={s.linkBtn} onClick={session.signOut} title="Signs this browser out of every staff site">Sign out</button>
        </div>
      </div>
      <div style={s.tabs}>
        {tabs.map(([key, label]) => (
          <button key={key} style={view === key && !jobId && !assetId && !newJob && !newAsset ? { ...s.tab, ...s.tabActive } : s.tab}
            onClick={() => { back(); setView(key); }}>{label}</button>
        ))}
      </div>
      <div style={view === 'tracker' && !jobId && !assetId && !newJob && !newAsset ? { ...s.body, ...s.bodyWide } : s.body}>
        {error && <p style={s.err}>{error}</p>}
        {!me ? <p style={s.muted}>Loading…</p> : body}
      </div>
    </div>
  );
}

// ── Labels ──
const STATUS_META = {
  new: { label: 'New', color: '#1a5fb4', bg: '#e8f0fb' },
  scheduled: { label: 'Scheduled', color: '#5b3cc4', bg: '#efeafd' },
  in_progress: { label: 'In progress', color: '#b26a00', bg: '#fff4e5' },
  on_hold: { label: 'On hold', color: '#8a5a00', bg: '#fdf0d5' },
  waiting_parts: { label: 'On hold', color: '#8a5a00', bg: '#fdf0d5' },
  done: { label: 'Done', color: '#1a7f37', bg: '#e6f4ea' },
  cancelled: { label: 'Cancelled', color: '#666', bg: '#f0f0f0' },
};
const PRIORITY_META = {
  urgent: { label: 'Urgent', color: '#fff', bg: '#b3261e' },
  high: { label: 'High', color: '#b3261e', bg: '#fdecea' },
  normal: { label: 'Normal', color: '#555', bg: '#f0f0f0' },
  low: { label: 'Low', color: '#777', bg: '#f7f7f7' },
};
const TYPE_LABELS = { repair: 'Repair', maintenance: 'PM', filter_change: 'Filter change', refill: 'HLF refill', inspection: 'Calibration / QC', site_inspection: 'Site inspection', estimate: 'Estimate', install: 'Installation', removal: 'Removal', other: 'Other' };
const OWNERSHIP_LABELS = { ours_loaned: 'Ours — on loan', ours_leased: 'Ours — leased', customer_owned: "Customer's own", illy_owned: "illy's", unknown: 'Not sure' };
// Whose machine a job is on, as the office's request form asks it.
const OWNER_LABELS = { illy: 'illy', ssrs: 'SSRS', customer: 'Customer' };
const OWNER_LONG = { illy: "illy's machine", ssrs: 'Our machine (SSRS)', customer: "Customer's machine" };
const OWNER_FROM_ASSET = { ours_loaned: 'ssrs', ours_leased: 'ssrs', customer_owned: 'customer', illy_owned: 'illy' };
const RECEIVED_VIA = { phone: 'Phone call', email: 'Email', in_person: 'In person' };
const ACTION_KINDS = { parts: 'Parts to order', customer: 'Tell the customer', other: 'Something else' };
// Same rule as the server (serviceDesk.isActionStep): a note step asking what
// still needs doing or ordering is emailed to the office when it's filled in.
const ACTION_STEP_RE = /action (is )?needed|parts? needed|special order parts|open issues/i;
const isActionStep = (it) => it.kind === 'text' && (it.actionItem === true || (it.actionItem === undefined && ACTION_STEP_RE.test(it.label || '')));
const RED = '#A50B18';

// The viewer's own calendar day (toISOString would be UTC — tomorrow, after 6pm in Colorado).
const todayIso = () => new Date().toLocaleDateString('en-CA');
function fmtDay(d) {
  if (!d) return '';
  const date = new Date(`${d}T12:00:00`);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
const desk_label = (n) => (n ? `J-${n}` : '');
const localDay = (iso) => (iso ? new Date(iso).toLocaleDateString('en-CA') : '');
const doneDay = (j) => localDay(j.closedAt) || j.scheduledFor || localDay(j.updatedAt);
const requestedDay = (j) => j.requestedOn || localDay(j.createdAt);
const returnedFrom = (r) => { const who = [r.assignedName, r.closedAt && `done ${fmtDay(localDay(r.closedAt))}`].filter(Boolean).join(', '); return who ? ` (${who})` : ''; };
const mapsUrl = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
const telUrl = (phone) => `tel:${String(phone).replace(/[^\d+]/g, '')}`;

function Pill({ meta }) {
  return meta ? <span style={{ ...s.pill, color: meta.color, background: meta.bg }}>{meta.label}</span> : null;
}

// ── Job cards ──
function cardWhen(job) {
  if (job.status === 'done') return `Done ${fmtDay(doneDay(job))}`;
  if (job.status === 'cancelled') return 'Cancelled';
  const overdue = job.scheduledFor && job.scheduledFor < todayIso();
  if (job.scheduledFor) return `${overdue ? 'Was due ' : ''}${fmtDay(job.scheduledFor)}${job.scheduledWindow ? ` · ${job.scheduledWindow}` : ''}`;
  return `Requested ${fmtDay(requestedDay(job))}`;
}

// `children` sit at the foot of the card (a booking form); clicks there stay there.
function JobCard({ job, onOpen, showAssignee, children }) {
  const overdue = job.scheduledFor && job.scheduledFor < todayIso() && !['done', 'cancelled'].includes(job.status);
  return (
    <div style={{ ...s.card, ...(job.returnOf ? s.cardReturn : {}) }} onClick={() => onOpen(job.jobId)} role="button" tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && onOpen(job.jobId)}>
      <div style={s.cardTop}>
        <span style={s.jobNo}>{job.label}</span>
        <Pill meta={STATUS_META[job.status]} />
        {job.priority !== 'normal' && <Pill meta={PRIORITY_META[job.priority]} />}
        {job.returnOf && <span style={s.returnFlag}>↩ Return visit{job.returnOf.number ? ` for J-${job.returnOf.number}` : ''}</span>}
        <span style={{ ...s.when, color: overdue ? '#b3261e' : '#777' }}>{cardWhen(job)}</span>
      </div>
      <div style={s.cardTitle}>{job.customerName}</div>
      {(job.assetLabel || job.equipmentOwner) && <div style={s.cardMachine}>{[job.assetLabel, OWNER_LONG[job.equipmentOwner]].filter(Boolean).join(' · ')}</div>}
      <div style={s.cardProblem}>{job.problem}</div>
      {job.availability && !job.scheduledFor && job.status !== 'done' && <div style={s.cardMachine}>Available: {job.availability}</div>}
      <div style={s.cardFoot}>
        <span>{TYPE_LABELS[job.type]}</span>
        {showAssignee && <span>{job.assignedName || 'Unassigned'}</span>}
        {job.noteCount > 0 && <span>{job.noteCount} note{job.noteCount === 1 ? '' : 's'}</span>}
        {job.photoCount > 0 && <span>{job.photoCount} photo{job.photoCount === 1 ? '' : 's'}</span>}
        {job.checklistProgress && <span style={{ color: job.checklistProgress.done === job.checklistProgress.total ? '#1a7f37' : '#888' }}>✓ {job.checklistProgress.done} of {job.checklistProgress.total} steps</span>}
        {job.openFollowUps > 0 && <span>{job.openFollowUps} follow-up{job.openFollowUps === 1 ? '' : 's'}</span>}
        {job.openActionItems > 0 && <span style={s.flagRed}>{job.openActionItems} action item{job.openActionItems === 1 ? '' : 's'} for the office</span>}
        {job.feedback
          ? <span style={job.feedback.satisfied ? s.flagGreen : s.flagRed}>{job.feedback.satisfied ? 'Customer happy' : 'Customer NOT happy'}{job.feedback.hasQuestions ? ' · has a question' : ''}</span>
          : job.feedbackAskedAt ? <span>Feedback asked {fmtDay(localDay(job.feedbackAskedAt))}</span> : null}
      </div>
      {children && <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">{children}</div>}
    </div>
  );
}

// ── My day ──
function MyDay({ ctx }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    serviceApi.jobs({ view: 'mine' }).then(setData).catch((e) => setError(e.message));
  }, [ctx.refreshKey]);
  if (error) return <p style={s.err}>{error}</p>;
  if (!data) return <p style={s.muted}>Loading your jobs…</p>;
  const g = data.groups;
  const total = g.overdue.length + g.today.length + g.upcoming.length + g.unscheduled.length;
  const section = (title, list, note) => list.length > 0 && (
    <section style={s.section} key={title}>
      <h3 style={s.h3}>{title} <span style={s.count}>{list.length}</span></h3>
      {note && <p style={s.muted}>{note}</p>}
      {list.map((j) => <JobCard key={j.jobId} job={j} onOpen={ctx.openJob} />)}
    </section>
  );
  return (
    <div>
      <div style={s.titleRow}>
        <h2 style={s.h2}>{fmtDay(data.today)}</h2>
        <button style={s.primarySm} onClick={() => ctx.setNewJob({ assignedTo: ctx.me.email })}>+ New job</button>
      </div>
      {(data.followUps || []).length > 0 && (
        <section style={s.section}>
          <h3 style={s.h3}>Follow-ups due <span style={s.count}>{data.followUps.length}</span></h3>
          {data.followUps.map((f) => (
            <div key={f.id} style={{ ...s.card, borderLeft: `4px solid ${f.dueDate < data.today ? '#b3261e' : RED}` }} role="button" tabIndex={0} onClick={() => ctx.openJob(f.jobId)}>
              <div style={s.cardTop}>
                <span style={s.jobNo}>{desk_label(f.number)}</span>
                <span style={{ ...s.when, color: f.dueDate < data.today ? '#b3261e' : '#777' }}>{f.dueDate < data.today ? `Was due ${fmtDay(f.dueDate)}` : 'Today'}</span>
              </div>
              <div style={s.cardTitle}>{f.customerName}</div>
              <div style={s.cardProblem}>{f.note}</div>
              {f.contactPhone && <div style={s.cardFoot}><a href={telUrl(f.contactPhone)} onClick={(e) => e.stopPropagation()}>Call {f.contactName || f.contactPhone}</a></div>}
            </div>
          ))}
        </section>
      )}
      {total === 0 && !(data.followUps || []).length && <div style={s.empty}>Nothing is assigned to you right now. New jobs show up here as soon as they're assigned.</div>}
      {section('Overdue', g.overdue, 'Booked for an earlier day and not finished.')}
      {section('Today', g.today)}
      {section('Coming up', g.upcoming)}
      {section('Not scheduled yet', g.unscheduled)}
    </div>
  );
}

// ── Requested: waiting to be planned ──
const PRIORITY_RANK = { urgent: 0, high: 1, normal: 2, low: 3 };
const matches = (j, needle) => !needle || [j.label, j.customerName, j.problem, j.assetLabel, j.contactName].some((v) => String(v || '').toLowerCase().includes(needle));

function RequestedBoard({ ctx }) {
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState('');
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => serviceApi.jobs().then((r) => setJobs(r.jobs.filter((j) => j.stage === 'requested'))).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load, ctx.refreshKey]);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (jobs || []).filter((j) => matches(j, needle))
      .sort((a, b) => (PRIORITY_RANK[a.priority] ?? 2) - (PRIORITY_RANK[b.priority] ?? 2) || requestedDay(a).localeCompare(requestedDay(b)));
  }, [jobs, q]);
  return (
    <div>
      <div style={s.titleRow}>
        <h2 style={s.h2}>Requested</h2>
        <button style={s.primarySm} onClick={() => ctx.setNewJob({})}>+ Log a request</button>
      </div>
      <p style={s.muted}>Waiting to be planned, most urgent and oldest first. Give a request a date and it moves to Scheduled / in progress.</p>
      <WebsiteRequests ctx={ctx} />
      {((jobs || []).length > 4 || q) && <input style={{ ...s.input, width: '100%', marginBottom: 12 }} placeholder="Search customer, machine, J-number…" value={q} onChange={(e) => setQ(e.target.value)} />}
      {error && <p style={s.err}>{error}</p>}
      {!jobs ? <p style={s.muted}>Loading…</p> : list.length === 0 ? <div style={s.empty}>{jobs.length ? 'No requests match.' : 'Nothing waiting to be planned.'}</div>
        : list.map((j) => (
          <JobCard key={j.jobId} job={j} onOpen={ctx.openJob} showAssignee>
            {booking === j.jobId
              ? <BookIt ctx={ctx} job={j} onCancel={() => setBooking(null)} onBooked={() => { setBooking(null); load(); ctx.refreshCounts(); }} />
              : <div style={s.inlineForm}><button style={s.outlineSm} onClick={() => setBooking(j.jobId)}>Book it</button></div>}
          </JobCard>
        ))}
    </div>
  );
}

// A date (and a technician and time, if known) straight from the list.
function BookIt({ ctx, job, onBooked, onCancel }) {
  const [form, setForm] = useState({ assignedTo: job.assignedTo || '', scheduledFor: '', scheduledWindow: job.scheduledWindow || '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function save(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try { await serviceApi.updateJob(job.jobId, { ...form, assignedTo: form.assignedTo || null }); onBooked(); }
    catch (err) { setError(err.message); setBusy(false); }
  }
  return (
    <form style={s.inlineForm} onSubmit={save}>
      <input style={s.input} type="date" value={form.scheduledFor} onChange={(e) => setForm((f) => ({ ...f, scheduledFor: e.target.value }))} aria-label="Date" autoFocus />
      <input style={{ ...s.input, width: 150 }} placeholder="Time, e.g. 9–11am" value={form.scheduledWindow} onChange={(e) => setForm((f) => ({ ...f, scheduledWindow: e.target.value }))} aria-label="Time" />
      <StaffSelect staff={ctx.staff} value={form.assignedTo} onChange={(v) => setForm((f) => ({ ...f, assignedTo: v || '' }))} />
      <button style={s.primarySm} type="submit" disabled={busy || !form.scheduledFor}>{busy ? 'Saving…' : 'Schedule'}</button>
      <button style={s.outlineSm} type="button" onClick={onCancel}>Cancel</button>
      {error && <p style={{ ...s.err, flexBasis: '100%' }}>{error}</p>}
    </form>
  );
}

// ── Scheduled / in progress: booked or started, until the work is done ──
function ScheduledBoard({ ctx }) {
  const [jobs, setJobs] = useState(null);
  const [who, setWho] = useState('all');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    serviceApi.jobs().then((r) => setJobs(r.jobs.filter((j) => j.stage === 'scheduled'))).catch((e) => setError(e.message));
  }, [ctx.refreshKey]);
  const today = todayIso();
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (jobs || []).filter((j) => {
      if (who === 'none' && j.assignedTo) return false;
      if (who !== 'all' && who !== 'none' && j.assignedTo !== who) return false;
      return matches(j, needle);
    }).sort((a, b) => String(a.scheduledFor || '9999').localeCompare(String(b.scheduledFor || '9999')) || String(a.assignedName || '').localeCompare(String(b.assignedName || '')));
    return [
      ['Past their day', list.filter((j) => j.scheduledFor && j.scheduledFor < today), 'Booked for an earlier day and not finished.'],
      ['Today', list.filter((j) => j.scheduledFor === today)],
      ['Coming up', list.filter((j) => j.scheduledFor > today)],
      ['Started, no date', list.filter((j) => !j.scheduledFor), 'Started (some since put on hold) without a booked day.'],
    ];
  }, [jobs, who, q, today]);
  return (
    <div>
      <div style={s.titleRow}><h2 style={s.h2}>Scheduled / in progress</h2></div>
      <div style={s.filters}>
        <select style={s.select} value={who} onChange={(e) => setWho(e.target.value)} aria-label="Technician">
          <option value="all">Everyone</option>
          {ctx.staff.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
          <option value="none">Unassigned</option>
        </select>
        <input style={{ ...s.input, flex: '1 1 200px' }} placeholder="Search customer, machine, J-number…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {error && <p style={s.err}>{error}</p>}
      {!jobs ? <p style={s.muted}>Loading…</p> : groups.every(([, list]) => !list.length) ? <div style={s.empty}>Nothing booked.</div>
        : groups.map(([title, list, note]) => list.length > 0 && (
          <section style={s.section} key={title}>
            <h3 style={{ ...s.h3, ...(title === 'Past their day' ? { color: '#b3261e' } : {}) }}>{title} <span style={s.count}>{list.length}</span></h3>
            {note && <p style={s.muted}>{note}</p>}
            {list.map((j) => <JobCard key={j.jobId} job={j} onOpen={ctx.openJob} showAssignee />)}
          </section>
        ))}
    </div>
  );
}

// ── Completed: by company, by day / week / month / year, by type ──
// Dated by the day the job was closed as done.
const PERIODS = { day: 'Day', week: 'Week', month: 'Month', year: 'Year', all: 'All' };
// The office's own list of job types first.
const TYPE_ORDER = ['maintenance', 'repair', 'site_inspection', 'install', 'other', ...Object.keys(TYPE_LABELS).filter((k) => !['maintenance', 'repair', 'site_inspection', 'install', 'other'].includes(k))];
const isoOf = (d) => d.toLocaleDateString('en-CA');
const atNoon = (day) => new Date(`${day}T12:00:00`);

function periodRange(period, anchor) {
  const d = atNoon(anchor);
  if (period === 'day') return { from: anchor, to: anchor, label: d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) };
  if (period === 'week') {
    const monday = new Date(d); monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    const short = (x) => x.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return { from: isoOf(monday), to: isoOf(sunday), label: `Week of ${short(monday)} – ${short(sunday)}, ${sunday.getFullYear()}` };
  }
  if (period === 'month') {
    return { from: isoOf(new Date(d.getFullYear(), d.getMonth(), 1, 12)), to: isoOf(new Date(d.getFullYear(), d.getMonth() + 1, 0, 12)), label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) };
  }
  if (period === 'year') return { from: `${d.getFullYear()}-01-01`, to: `${d.getFullYear()}-12-31`, label: String(d.getFullYear()) };
  return null;
}

function shiftAnchor(period, anchor, step) {
  const d = atNoon(anchor);
  if (period === 'day') d.setDate(d.getDate() + step);
  if (period === 'week') d.setDate(d.getDate() + 7 * step);
  if (period === 'month') { d.setDate(1); d.setMonth(d.getMonth() + step); }
  if (period === 'year') d.setFullYear(d.getFullYear() + step);
  return isoOf(d);
}

function CompletedBoard({ ctx }) {
  const [jobs, setJobs] = useState(null);
  const [show, setShow] = useState('done');
  const [company, setCompany] = useState('');
  const [period, setPeriod] = useState('month');
  const [anchor, setAnchor] = useState(todayIso());
  const [type, setType] = useState('');
  const [who, setWho] = useState('all');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    serviceApi.jobs({ view: 'closed', days: 3650 }).then((r) => setJobs(r.jobs)).catch((e) => setError(e.message));
  }, [ctx.refreshKey]);
  const companies = useMemo(() => [...new Set((jobs || []).filter((j) => j.status === show).map((j) => j.customerName).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [jobs, show]);
  const range = useMemo(() => periodRange(period, anchor), [period, anchor]);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (jobs || []).filter((j) => {
      if (j.status !== show) return false;
      const day = doneDay(j);
      if (range && (day < range.from || day > range.to)) return false;
      if (company && j.customerName !== company) return false;
      if (type && j.type !== type) return false;
      if (who === 'none' && j.assignedTo) return false;
      if (who !== 'all' && who !== 'none' && j.assignedTo !== who) return false;
      return matches(j, needle);
    }).sort((a, b) => doneDay(b).localeCompare(doneDay(a)) || (b.number || 0) - (a.number || 0));
  }, [jobs, show, range, company, type, who, q]);
  const toInvoice = show === 'done' ? list.filter((j) => j.billingStatus === 'to_invoice').length : 0;
  const returns = list.filter((j) => j.returnOf).length;
  const happy = list.filter((j) => j.feedback && j.feedback.satisfied).length;
  const unhappy = list.filter((j) => j.feedback && !j.feedback.satisfied).length;
  return (
    <div>
      <div style={s.titleRow}>
        <h2 style={s.h2}>{show === 'done' ? 'Completed' : 'Cancelled'}</h2>
        <button style={s.linkish} onClick={() => setShow(show === 'done' ? 'cancelled' : 'done')}>{show === 'done' ? 'Show cancelled jobs' : 'Back to completed'}</button>
      </div>
      <div style={s.filters}>
        <select style={{ ...s.select, flex: '1 1 220px' }} value={company} onChange={(e) => setCompany(e.target.value)} aria-label="Company">
          <option value="">Every company</option>
          {companies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={s.select} value={type} onChange={(e) => setType(e.target.value)} aria-label="Type">
          <option value="">Every type</option>
          {TYPE_ORDER.map((k) => <option key={k} value={k}>{TYPE_LABELS[k]}</option>)}
        </select>
        <select style={s.select} value={who} onChange={(e) => setWho(e.target.value)} aria-label="Technician">
          <option value="all">Every technician</option>
          {ctx.staff.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
          <option value="none">Nobody assigned</option>
        </select>
      </div>
      <div style={s.filters}>
        <div style={s.segment}>
          {Object.entries(PERIODS).map(([k, label]) => (
            <button key={k} style={period === k ? { ...s.segBtn, ...s.segActive } : s.segBtn} onClick={() => setPeriod(k)}>{label}</button>
          ))}
        </div>
        {range && (
          <div style={s.periodNav}>
            <button style={s.xBtn} onClick={() => setAnchor(shiftAnchor(period, anchor, -1))} aria-label="Earlier">‹</button>
            <span style={s.periodLabel}>{range.label}</span>
            <button style={s.xBtn} onClick={() => setAnchor(shiftAnchor(period, anchor, 1))} aria-label="Later">›</button>
            <input style={{ ...s.input, width: 150 }} type="date" value={anchor} onChange={(e) => e.target.value && setAnchor(e.target.value)} aria-label="Go to date" />
          </div>
        )}
      </div>
      <input style={{ ...s.input, width: '100%', marginBottom: 12 }} placeholder="Search customer, machine, J-number…" value={q} onChange={(e) => setQ(e.target.value)} />
      {error && <p style={s.err}>{error}</p>}
      {jobs && (
        <p style={s.muted}>
          {list.length} {show === 'done' ? 'completed' : 'cancelled'}{returns ? ` · ${returns} return visit${returns === 1 ? '' : 's'}` : ''}
          {happy || unhappy ? ` · feedback: ${happy} happy, ${unhappy} not happy` : ''}
        </p>
      )}
      {toInvoice > 0 && (
        <div style={s.notice}>
          {toInvoice} billable job{toInvoice === 1 ? '' : 's'} waiting to be invoiced in QuickBooks.{' '}
          {ctx.me?.canBill ? "Once it's billed, link each one's invoice: open the job, or use the Tracker." : "The office links each one's invoice once it's billed."}
        </div>
      )}
      {!jobs ? <p style={s.muted}>Loading…</p> : list.length === 0 ? <div style={s.empty}>No {show === 'done' ? 'completed' : 'cancelled'} jobs match.</div>
        : list.map((j) => <JobCard key={j.jobId} job={j} onOpen={ctx.openJob} showAssignee />)}
    </div>
  );
}

// ── Tracker ──
// Every job on one page, open and closed: the office's replacement for the
// service tracker spreadsheet. A red cell is a blank someone has to fill — no
// MaintainX ticket, nobody on the job, an open job with no date, finished work
// with no invoice. The server decides what counts as a gap (serviceDesk.trackerRow).
const GAPS = [
  ['ticket', 'No MaintainX ticket'],
  ['technician', 'No technician'],
  ['scheduled', 'Not scheduled'],
  ['invoice', 'Done, not invoiced'],
];
const TRACKER_PERIODS = { year: 'This year', '12m': 'Last 12 months', all: 'All time' };
const TRACKER_STATUSES = { live: 'All but cancelled', open: 'Open', done: 'Done', cancelled: 'Cancelled' };
const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function fmtShortDay(d, thisYear) {
  if (!d) return '';
  const date = new Date(`${d}T12:00:00`);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(d.slice(0, 4) !== thisYear ? { year: 'numeric' } : {}) });
}

// The day an invoice's balance was read from QuickBooks, YYYY-MM-DD.
const balanceDay = (inv) => (inv && inv.balanceAt ? new Date(inv.balanceAt).toLocaleDateString('en-CA') : '');
const markedInvoiced = (r) => r.missing.invoice && r.billingStatus === 'invoiced';

function trackerCsv(rows) {
  const head = ['Job', 'Customer', 'Requested', 'Scheduled', 'Type', 'Technician', 'Status', 'MaintainX ticket', 'MaintainX link', 'QuickBooks invoice', 'Invoice link', 'Billing', 'Office note'];
  const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map((r) => [
    r.label, r.customerName, r.requestedOn, r.scheduledFor, TYPE_LABELS[r.type] || r.type, r.assignedName,
    STATUS_META[r.status]?.label || r.status, r.ticket ? `#${r.ticket.number}` : '', r.ticket?.url,
    r.invoice?.number, r.invoice?.url,
    r.billingStatus === 'no_charge' ? 'No charge' : r.invoice ? 'Invoiced' : markedInvoiced(r) ? 'Marked invoiced, no invoice linked' : r.missing.invoice ? 'Not invoiced' : '',
    r.trackerNote,
  ].map(cell).join(','));
  return [head.map(cell).join(','), ...lines].join('\n');
}

function downloadCsv(name, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// The row's billing after a change on the job, without reloading every job.
function withBilling(row, job) {
  const inv = job.invoice;
  return {
    ...row,
    billingStatus: job.billingStatus,
    invoice: inv ? { number: inv.number, url: inv.url, date: inv.date, total: inv.total, balance: inv.balance, balanceAt: inv.balanceAt || job.billingSetAt } : null,
    missing: { ...row.missing, invoice: row.status === 'done' && !inv && job.billingStatus !== 'no_charge' },
  };
}

function Tracker({ ctx }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [gap, setGap] = useState('');
  const [period, setPeriod] = useState('year');
  const [status, setStatus] = useState('live');
  const [type, setType] = useState('');
  const [tech, setTech] = useState('');
  const [q, setQ] = useState('');
  const [linking, setLinking] = useState(null);   // jobId whose invoice is being linked
  useEffect(() => {
    serviceApi.tracker().then(setData).catch((e) => setError(e.message));
  }, [ctx.refreshKey]);

  const techNames = useMemo(() => [...new Set((data?.rows || []).flatMap((r) => String(r.assignedName || '').split(', ').filter(Boolean)))].sort(), [data]);
  const base = useMemo(() => {
    if (!data) return [];
    const yearAgo = `${Number(data.today.slice(0, 4)) - 1}${data.today.slice(4)}`;
    const from = period === 'year' ? `${data.today.slice(0, 4)}-01-01` : period === '12m' ? yearAgo : '';
    const needle = q.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (from && String(r.requestedOn || '') < from) return false;
      if (status === 'live' && r.status === 'cancelled') return false;
      if (status === 'open' && ['done', 'cancelled'].includes(r.status)) return false;
      if ((status === 'done' || status === 'cancelled') && r.status !== status) return false;
      if (type && r.type !== type) return false;
      if (tech === 'none' && (r.assignedTo || r.assignedName)) return false;
      if (tech && tech !== 'none' && !String(r.assignedName || '').split(', ').includes(tech)) return false;
      if (!needle) return true;
      return [r.label, r.customerName, r.invoice?.number, r.ticket ? `#${r.ticket.number}` : '', r.trackerNote].some((v) => String(v || '').toLowerCase().includes(needle));
    }).sort((a, b) => String(b.requestedOn || '').localeCompare(String(a.requestedOn || '')) || (b.number || 0) - (a.number || 0));
  }, [data, period, status, type, tech, q]);
  const counts = useMemo(() => Object.fromEntries(GAPS.map(([k]) => [k, base.filter((r) => r.missing[k]).length])), [base]);
  const rows = gap ? base.filter((r) => r.missing[gap]) : base;

  if (error) return <p style={s.err}>{error}</p>;
  if (!data) return <p style={s.muted}>Loading every job…</p>;
  const thisYear = data.today.slice(0, 4);
  const replaceRow = (job) => setData((d) => ({ ...d, rows: d.rows.map((r) => (r.jobId === job.jobId ? withBilling(r, job) : r)) }));
  const missing = (text) => <td style={{ ...s.trTd, ...s.gapCell }}>{text}</td>;
  const stop = (e) => e.stopPropagation();

  return (
    <div>
      <div style={s.titleRow}>
        <h2 style={s.h2}>Tracker</h2>
        <button style={s.outlineSm} onClick={() => downloadCsv(`service-tracker-${data.today}.csv`, trackerCsv(rows))}>Download CSV</button>
      </div>
      <div style={s.gapChips}>
        <button style={!gap ? { ...s.gapChip, ...s.gapChipOn } : s.gapChip} onClick={() => setGap('')}>All jobs <b>{base.length}</b></button>
        {GAPS.map(([k, label]) => (
          <button key={k} style={gap === k ? { ...s.gapChip, ...s.gapChipOn } : s.gapChip} onClick={() => setGap(gap === k ? '' : k)}>
            {label} <b style={counts[k] ? { color: '#a32020' } : undefined}>{counts[k]}</b>
          </button>
        ))}
      </div>
      <div style={s.filters}>
        <select style={s.select} value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Requested">
          {Object.entries(TRACKER_PERIODS).map(([k, v]) => <option key={k} value={k}>Requested: {v}</option>)}
        </select>
        <select style={s.select} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          {Object.entries(TRACKER_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select style={s.select} value={type} onChange={(e) => setType(e.target.value)} aria-label="Job type">
          <option value="">Every type</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select style={s.select} value={tech} onChange={(e) => setTech(e.target.value)} aria-label="Technician">
          <option value="">Every technician</option>
          {techNames.map((n) => <option key={n} value={n}>{n}</option>)}
          <option value="none">Nobody assigned</option>
        </select>
        <input style={{ ...s.input, flex: '1 1 180px' }} placeholder="Customer, J-number, ticket, invoice or note…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {rows.length === 0 ? <div style={s.empty}>No jobs match.</div> : (
        <div style={s.tableScroll}>
          <table style={s.trTable}>
            <thead><tr>
              <th style={s.trTh}>Job</th><th style={s.trTh}>Customer</th><th style={s.trTh}>Requested</th><th style={s.trTh}>Scheduled</th>
              <th style={s.trTh}>Type</th><th style={s.trTh}>Technician</th><th style={s.trTh}>Status</th>
              <th style={s.trTh}>MaintainX</th><th style={s.trTh}>QuickBooks invoice</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => [
                <tr key={r.jobId} style={s.trRow} onClick={() => ctx.openJob(r.jobId)}>
                  <td style={{ ...s.trTd, ...s.trackJob }}>{r.label}</td>
                  <td style={{ ...s.trTd, minWidth: 160, maxWidth: 280 }}>
                    {r.customerName || <span style={s.trNote}>No customer</span>}
                    {r.trackerNote && <div style={s.trNote} title={r.trackerNote}>{r.trackerNote.length > 80 ? `${r.trackerNote.slice(0, 80)}…` : r.trackerNote}</div>}
                  </td>
                  <td style={s.trTdNw}>{fmtShortDay(r.requestedOn, thisYear)}</td>
                  {r.missing.scheduled ? missing('Not booked') : <td style={s.trTdNw}>{r.scheduledFor ? fmtShortDay(r.scheduledFor, thisYear) : <span style={s.trNote}>—</span>}</td>}
                  <td style={s.trTdNw}>{TYPE_LABELS[r.type] || r.type}</td>
                  {r.missing.technician ? missing('Nobody') : <td style={{ ...s.trTd, minWidth: 120 }}>{r.assignedName}</td>}
                  <td style={s.trTdNw}><Pill meta={STATUS_META[r.status]} /></td>
                  {r.missing.ticket ? missing('No ticket') : (
                    <td style={s.trTdNw}>{r.ticket ? <a href={r.ticket.url} target="_blank" rel="noreferrer" onClick={stop}>#{r.ticket.number}</a> : <span style={s.trNote}>—</span>}</td>
                  )}
                  <InvoiceCell row={r} today={data.today} canBill={data.canBill} open={linking === r.jobId} onLink={() => setLinking(linking === r.jobId ? null : r.jobId)} />
                </tr>,
                linking === r.jobId && (
                  <tr key={`${r.jobId}-link`}>
                    <td colSpan={9} style={s.trDetail}>
                      <InvoiceLinker jobId={r.jobId} done={r.status === 'done'} billed={r.invoice ? 'invoice' : r.billingStatus === 'no_charge' ? 'no_charge' : null} onDone={(job) => { replaceRow(job); setLinking(null); }} onCancel={() => setLinking(null)} />
                    </td>
                  </tr>
                ),
              ])}
            </tbody>
          </table>
        </div>
      )}
      <p style={s.muted}>{rows.length} job{rows.length === 1 ? '' : 's'}. Requested is the day the request came in; for jobs copied from MaintainX it's the day the work order was made.</p>
    </div>
  );
}

// The server re-reads balances still owed when the Tracker loads, so an open
// balance dated before today means QuickBooks didn't answer.
function InvoiceCell({ row, today, canBill, open, onLink }) {
  const stop = (e) => e.stopPropagation();
  const linkBtn = (label) => canBill && <button style={s.cellBtn} onClick={(e) => { stop(e); onLink(); }}>{open ? 'Close' : label}</button>;
  if (row.invoice) {
    const asOf = balanceDay(row.invoice);
    return (
      <td style={s.trTdNw}>
        <a href={row.invoice.url} target="_blank" rel="noreferrer" onClick={stop}>#{row.invoice.number}</a>
        {row.invoice.total != null && (
          <span style={s.trNote}> · {money(row.invoice.total)}{row.invoice.balance > 0 ? ` open${asOf && asOf !== today ? ` as of ${fmtShortDay(asOf, today.slice(0, 4))}` : ''}` : ' paid'}</span>
        )}
        {' '}{linkBtn('Change')}
      </td>
    );
  }
  if (row.billingStatus === 'no_charge') return <td style={s.trTdNw}><span style={s.trNote}>No charge</span> {linkBtn('Change')}</td>;
  if (markedInvoiced(row)) return <td style={{ ...s.trTd, ...s.gapCell }}>Marked invoiced, no invoice linked {linkBtn('Add')}</td>;
  if (row.missing.invoice) return <td style={{ ...s.trTd, ...s.gapCell }}>Not invoiced {linkBtn('Add')}</td>;
  return <td style={s.trTdNw}><span style={s.trNote}>—</span> {linkBtn('Add')}</td>;
}

// Type the invoice number, check it's the right one, link it. The server looks
// it up in QuickBooks again when linking, so what's stored is what QuickBooks says.
function InvoiceLinker({ jobId, done, billed, onDone, onCancel }) {
  const [number, setNumber] = useState('');
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function run(fn) {
    setBusy(true); setError('');
    try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  const find = () => run(async () => { setFound(null); setFound((await serviceApi.findInvoice(number.trim())).invoice); });
  const change = (body) => run(async () => onDone((await serviceApi.setBilling(jobId, body)).job));
  return (
    <div style={s.linker} onClick={(e) => e.stopPropagation()}>
      <form style={s.inlineForm} onSubmit={(e) => { e.preventDefault(); if (number.trim()) find(); }}>
        <input style={{ ...s.input, width: 200 }} inputMode="numeric" placeholder="Invoice number" value={number} autoFocus
          onChange={(e) => { setNumber(e.target.value); setFound(null); }} aria-label="QuickBooks invoice number" />
        <button type="submit" style={s.outlineSm} disabled={busy || !number.trim()}>{busy && !found ? 'Looking…' : 'Find in QuickBooks'}</button>
        {done && <button type="button" style={s.outlineSm} disabled={busy} onClick={() => change({ noCharge: true })}>No charge</button>}
        {billed && <button type="button" style={s.statusBtnQuiet} disabled={busy} onClick={() => change({ clear: true })}>{billed === 'no_charge' ? 'Undo no charge' : 'Unlink invoice'}</button>}
        {onCancel && <button type="button" style={s.linkish} onClick={onCancel}>Cancel</button>}
      </form>
      {found && (
        <div style={s.found}>
          <div><b>Invoice {found.number}</b> · {found.customerName || 'no customer name'} · {money(found.total)} · {fmtShortDay(found.date, '')} · {found.status}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button style={s.primarySm} disabled={busy} onClick={() => change({ invoiceNumber: found.number })}>{busy ? 'Linking…' : 'Link to this job'}</button>
            <a href={found.url} target="_blank" rel="noreferrer" style={{ ...s.outlineSm, textDecoration: 'none' }}>Open in QuickBooks</a>
          </div>
        </div>
      )}
      {error && <p style={s.err}>{error}</p>}
    </div>
  );
}

// ── Pickers ──
function CustomerPicker({ customers, value, onChange }) {
  const label = (c) => `${c.name} · #${c.id}`;
  const current = customers.find((c) => c.id === value);
  const [text, setText] = useState(current ? label(current) : '');
  return (
    <>
      <input style={s.input} list="svc-customers" placeholder="Start typing a customer name…" value={text}
        onChange={(e) => {
          setText(e.target.value);
          const m = e.target.value.match(/#(\w+)$/);
          const hit = m && customers.find((c) => c.id === m[1]);
          onChange(hit || null);
        }} />
      <datalist id="svc-customers">
        {customers.map((c) => <option key={c.id} value={label(c)} />)}
      </datalist>
    </>
  );
}

function StaffSelect({ staff, value, onChange }) {
  return (
    <select style={s.select} value={value || ''} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">Unassigned</option>
      {staff.map((p) => <option key={p.email} value={p.email}>{p.name}{p.role === 'technician' ? '' : ' (office)'}</option>)}
    </select>
  );
}

function Field({ label, children, hint }) {
  return (
    <label style={s.field}>
      <span style={s.fieldLabel}>{label}</span>
      {children}
      {hint && <span style={s.hint}>{hint}</span>}
    </label>
  );
}

// ── New job ──
// Adds a machine for this customer without leaving the form it sits in, then
// picks it. The rest of the machine's record (warranty, service interval,
// notes) is on the Equipment tab. It lives inside another form, so Enter
// saves the machine rather than submitting the job.
function QuickAddMachine({ customer, onAdded, initialName = '' }) {
  const blank = { name: '', make: '', model: '', serial: '', location: '' };
  const [open, setOpen] = useState(false);
  const [m, setM] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!customer) return null;
  if (!open) {
    return <button type="button" style={{ ...s.linkish, justifySelf: 'start', textAlign: 'left' }} onClick={() => { setM({ ...blank, name: initialName }); setError(''); setOpen(true); }}>+ Add a machine</button>;
  }
  const setField = (k) => (e) => setM((x) => ({ ...x, [k]: e.target.value }));
  async function save() {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const { asset } = await serviceApi.createAsset({ ...m, customerId: customer.id, customerName: customer.name });
      onAdded(asset);
      setOpen(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  const enterSaves = (e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } };
  return (
    <div style={s.quickAdd} onKeyDown={enterSaves}>
      <div style={s.fieldLabel}>New machine at {customer.name}</div>
      <div style={s.row2}>
        <Field label="Name"><input style={s.input} value={m.name} onChange={setField('name')} placeholder="e.g. Bar espresso machine" autoFocus /></Field>
        <Field label="Where in the venue"><input style={s.input} value={m.location} onChange={setField('location')} placeholder="e.g. Lobby bar" /></Field>
      </div>
      <div style={s.row2}>
        <Field label="Make"><input style={s.input} value={m.make} onChange={setField('make')} placeholder="e.g. Simonelli" /></Field>
        <Field label="Model"><input style={s.input} value={m.model} onChange={setField('model')} placeholder="e.g. Aurelia Wave 2 group" /></Field>
        <Field label="Serial number"><input style={s.input} value={m.serial} onChange={setField('serial')} /></Field>
      </div>
      {error && <p style={s.err}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" style={s.primarySm} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save machine'}</button>
        <button type="button" style={s.outlineSm} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

// The company on a request: a customer from the list, or a name typed in for
// someone who isn't a customer yet.
function CompanyPicker({ customers, value, onChange }) {
  const label = (c) => `${c.name} · #${c.id}`;
  const [text, setText] = useState(value.customer ? label(value.customer) : value.name || '');
  return (
    <>
      <input style={s.input} list="svc-companies" placeholder="Start typing a customer name…" value={text}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          const m = t.match(/#(\w+)$/);
          const hit = m && customers.find((c) => c.id === m[1]);
          onChange(hit ? { customer: hit, name: hit.name } : { customer: null, name: t.trim() });
        }} />
      <datalist id="svc-companies">
        {customers.map((c) => <option key={c.id} value={label(c)} />)}
      </datalist>
      {!value.customer && value.name && <span style={s.hint}>Not picked from the customer list, so it's saved under this name. Pick from the list if they're a customer.</span>}
    </>
  );
}

function Choice({ options, value, onChange }) {
  return (
    <div style={s.segRow}>
      {Object.entries(options).map(([k, label]) => (
        <button key={k} type="button" style={value === k ? { ...s.seg, ...s.segOn } : s.seg} onClick={() => onChange(value === k ? '' : k)}>{label}</button>
      ))}
    </div>
  );
}

// The office's request form, in the order of the paper one they used:
// company, contact, whose machine, equipment, problem, availability, address.
// Saved without a date it waits under Requested; with one it's scheduled.
// Also used for a return visit (prefill.returnOfJob is the job being redone).
function RequestForm({ ctx, prefill }) {
  const { returnOfJob: original, ...preset } = prefill;
  // A return visit keeps the job's customer even if it has left the list.
  const [company, setCompany] = useState(() => {
    const c = ctx.customers.find((x) => x.id === preset.customerId)
      || (preset.customerId ? { id: preset.customerId, name: preset.customerName || '', phone: '', siteAddress: '' } : null);
    return c ? { customer: c, name: c.name } : { customer: null, name: preset.customerName || '' };
  });
  const customer = company.customer;
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({
    contactName: '', contactPhone: '', contactEmail: '', equipmentOwner: '', assetId: '', assetLabel: '', problem: '', availability: '', siteAddress: '',
    receivedVia: '', type: 'repair', priority: 'normal', assignedTo: '', scheduledFor: '', scheduledWindow: '',
    ...Object.fromEntries(Object.entries(preset).filter(([k]) => !['customerId', 'customerName'].includes(k))),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // What the chosen company filled in. Switching company replaces those, and
  // the machine (it belonged to the last one), but not what was typed by hand.
  const filled = useRef({ id: customer ? customer.id : null, contactPhone: '', siteAddress: '', equipmentOwner: '' });
  useEffect(() => {
    const last = filled.current;
    const id = customer ? customer.id : null;
    const changed = id !== last.id;
    const fill = { contactPhone: (customer && customer.phone) || '', siteAddress: (customer && customer.siteAddress) || '' };
    setForm((f) => {
      const next = { ...f };
      for (const k of ['contactPhone', 'siteAddress']) if (!f[k] || (changed && f[k] === last[k])) next[k] = fill[k];
      if (changed) {
        next.assetId = '';
        if (f.equipmentOwner && f.equipmentOwner === last.equipmentOwner) next.equipmentOwner = '';
      }
      return next;
    });
    filled.current = { id, ...fill, equipmentOwner: changed ? '' : last.equipmentOwner };
    if (!customer) { setAssets([]); return; }
    serviceApi.assets({ customerId: customer.id }).then((r) => setAssets(r.assets.filter((a) => a.active !== false))).catch(() => setAssets([]));
  }, [customer]);

  function pickMachine(assetId) {
    const a = assets.find((x) => x.assetId === assetId);
    const owner = (a && OWNER_FROM_ASSET[a.ownership]) || '';
    setForm((f) => {
      if (f.equipmentOwner || !owner) return { ...f, assetId };
      filled.current = { ...filled.current, equipmentOwner: owner };
      return { ...f, assetId, equipmentOwner: owner };
    });
  }

  async function submit(e) {
    e.preventDefault();
    if (!customer && !company.name) { setError('Pick a customer from the list, or type the company name.'); return; }
    setBusy(true); setError('');
    try {
      const { job } = await serviceApi.createJob({
        ...form, customerId: customer ? customer.id : '', customerName: customer ? customer.name : company.name,
        ...(original ? { returnOf: original.jobId } : {}),
      });
      ctx.openJob(job.jobId);
    } catch (err) { setError(err.message); setBusy(false); }
  }

  return (
    <form onSubmit={submit}>
      <button type="button" style={s.backBtn} onClick={ctx.back}>← Back</button>
      <h2 style={s.h2}>{original ? `Return visit for ${original.label}` : 'Log a request'}</h2>
      {original
        ? <div style={s.notice}>The work on {original.label} ({TYPE_LABELS[original.type] || 'job'}{original.assignedName ? ` by ${original.assignedName}` : ''}, done {fmtDay(doneDay(original))}) needs another visit. This new job is flagged as a return visit and linked to it.</div>
        : <p style={s.muted}>For requests that come in by phone or email. Leave the date empty and it waits under Requested until it's planned.</p>}
      <div style={s.formGrid}>
        <Field label="Company"><CompanyPicker customers={ctx.customers} value={company} onChange={setCompany} /></Field>
        <Field label="Contact">
          <div style={s.row3}>
            <input style={s.input} placeholder="Name" value={form.contactName} onChange={(e) => set('contactName', e.target.value)} aria-label="Contact name" />
            <input style={s.input} type="tel" placeholder="Phone" value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} aria-label="Contact phone" />
            <input style={s.input} type="email" placeholder="Email (for the feedback form)" value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} aria-label="Contact email" />
          </div>
        </Field>
        <Field label="Whose machine"><Choice options={OWNER_LABELS} value={form.equipmentOwner} onChange={(v) => set('equipmentOwner', v)} /></Field>
        <Field label="Equipment">
          {customer && assets.length > 0 && (
            <select style={s.select} value={form.assetId} onChange={(e) => pickMachine(e.target.value)}>
              <option value="">Not listed / not sure</option>
              {assets.map((a) => <option key={a.assetId} value={a.assetId}>{[a.name || [a.make, a.model].filter(Boolean).join(' '), a.location && `(${a.location})`].filter(Boolean).join(' ')}</option>)}
            </select>
          )}
          {!form.assetId && <input style={s.input} value={form.assetLabel} onChange={(e) => set('assetLabel', e.target.value)} placeholder="e.g. 2-group espresso machine, bar" />}
        </Field>
        <QuickAddMachine customer={customer} initialName={form.assetLabel}
          onAdded={(a) => { setAssets((list) => [...list, a]); setForm((f) => ({ ...f, assetId: a.assetId, assetLabel: '' })); }} />
        <Field label="Problem">
          <textarea style={s.textarea} rows={4} value={form.problem} onChange={(e) => set('problem', e.target.value)} placeholder={original ? 'What still isn\'t working?' : "What's wrong, or what needs doing"} />
        </Field>
        <Field label="Availability" hint="When the customer can have us, e.g. weekdays before 10am, not Fridays">
          <input style={s.input} value={form.availability} onChange={(e) => set('availability', e.target.value)} />
        </Field>
        <Field label="Address / city"><input style={s.input} value={form.siteAddress} onChange={(e) => set('siteAddress', e.target.value)} /></Field>
        {!original && <Field label="How it came in"><Choice options={RECEIVED_VIA} value={form.receivedVia} onChange={(v) => set('receivedVia', v)} /></Field>}
        <div style={s.row2}>
          <Field label="Type">
            <select style={s.select} value={form.type} onChange={(e) => set('type', e.target.value)}>
              {TYPE_ORDER.map((k) => <option key={k} value={k}>{TYPE_LABELS[k]}</option>)}
            </select>
          </Field>
          <Field label="Priority">
            <select style={s.select} value={form.priority} onChange={(e) => set('priority', e.target.value)}>
              {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
        </div>
        <div style={s.bookBox}>
          <div style={s.fieldLabel}>Book it now (optional)</div>
          <div style={s.row3}>
            <input style={s.input} type="date" value={form.scheduledFor} onChange={(e) => set('scheduledFor', e.target.value)} aria-label="Date" />
            <input style={s.input} value={form.scheduledWindow} onChange={(e) => set('scheduledWindow', e.target.value)} placeholder="Time, e.g. 9–11am" aria-label="Time" />
            <StaffSelect staff={ctx.staff} value={form.assignedTo} onChange={(v) => set('assignedTo', v || '')} />
          </div>
        </div>
      </div>
      {error && <p style={s.err}>{error}</p>}
      <button style={s.primary} type="submit" disabled={busy}>{busy ? 'Saving…' : form.scheduledFor ? 'Save and schedule' : 'Save request'}</button>
    </form>
  );
}

// ── One job ──
const EDIT_KEYS = ['assignedTo', 'requestedOn', 'scheduledFor', 'scheduledWindow', 'type', 'priority', 'problem', 'resolution', 'timeMinutes', 'partsUsed', 'billable', 'signedBy', 'contactName', 'contactPhone', 'contactEmail', 'siteAddress', 'equipmentOwner', 'availability'];

function JobDetail({ ctx, jobId }) {
  const [job, setJob] = useState(null);
  const [asset, setAsset] = useState(null);
  const [request, setRequest] = useState(null);
  const [returnVisits, setReturnVisits] = useState([]);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  const take = useCallback((j) => {
    setJob(j);
    setDraft(Object.fromEntries(EDIT_KEYS.map((k) => [k, k === 'partsUsed' ? (j.partsUsed || []).map((p) => ({ ...p })) : j[k] ?? (k === 'billable' ? false : '')])));
  }, []);

  useEffect(() => {
    serviceApi.job(jobId).then((r) => { take(r.job); setAsset(r.asset); setRequest(r.request); setReturnVisits(r.returnVisits || []); }).catch((e) => setError(e.message));
  }, [jobId, take]);

  if (error && !job) return <div><button style={s.backBtn} onClick={ctx.back}>← Back</button><p style={s.err}>{error}</p></div>;
  if (!job || !draft) return <p style={s.muted}>Loading job…</p>;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const changed = () => Object.fromEntries(EDIT_KEYS.filter((k) => JSON.stringify(draft[k] ?? '') !== JSON.stringify(job[k] ?? (k === 'billable' ? false : k === 'partsUsed' ? [] : ''))).map((k) => [k, draft[k]]));
  const dirty = Object.keys(changed()).length > 0;

  async function save(extra = {}) {
    setBusy(true); setError(''); setSaved('');
    try {
      const { job: next } = await serviceApi.updateJob(job.jobId, { ...changed(), ...extra });
      take(next);
      setSaved('Saved');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  async function setStatus(status) {
    if (status === 'done' && !String(draft.resolution || '').trim()) {
      setError('Write what was done under "What was done", then press Done.');
      return;
    }
    await save({ status });
  }
  async function addNote() {
    if (!note.trim()) return;
    setBusy(true); setError('');
    try { const { job: next } = await serviceApi.addNote(job.jobId, note); setJob(next); setNote(''); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  async function addPhoto(file) {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const image = await resizeToJpeg(await readFileAsDataUrl(file), 1600, 0.75);
      const { job: next } = await serviceApi.addPhoto(job.jobId, image);
      setJob(next);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const closed = ['done', 'cancelled'].includes(job.status);
  const billingSet = !!job.invoice || job.billingStatus === 'no_charge';
  const parts = draft.partsUsed || [];
  return (
    <div>
      <button style={s.backBtn} onClick={ctx.back}>← Back</button>
      <div style={s.detailHead}>
        <div>
          <div style={s.cardTop}>
            <span style={s.jobNo}>{job.label}</span>
            <Pill meta={STATUS_META[job.status]} />
            <Pill meta={PRIORITY_META[job.priority]} />
          </div>
          <h2 style={{ ...s.h2, marginTop: 6 }}>{job.customerName}</h2>
        </div>
      </div>
      {job.returnOf && (
        <div style={s.notice}>
          ↩ Return visit: the work on <button style={s.linkish} onClick={() => ctx.openJob(job.returnOf.jobId)}>{desk_label(job.returnOf.number) || 'the earlier job'}</button>
          {returnedFrom(job.returnOf)} didn't hold.
        </div>
      )}

      <div style={s.contactRow}>
        {job.siteAddress && <a style={s.contactBtn} href={mapsUrl(job.siteAddress)} target="_blank" rel="noreferrer">Directions</a>}
        {job.contactPhone && <a style={s.contactBtn} href={telUrl(job.contactPhone)}>Call {job.contactName || 'customer'}</a>}
      </div>
      {job.siteAddress && <div style={s.muted}>{job.siteAddress}</div>}

      <div style={s.statusRow}>
        {!closed && job.status !== 'in_progress' && <button style={s.statusBtn} disabled={busy} onClick={() => setStatus('in_progress')}>Start</button>}
        {!closed && job.status !== 'on_hold' && job.status !== 'waiting_parts' && <button style={s.statusBtn} disabled={busy} onClick={() => setStatus('on_hold')}>On hold</button>}
        {!closed && <button style={{ ...s.statusBtn, ...s.statusDone }} disabled={busy} onClick={() => setStatus('done')}>Done</button>}
        {!closed && <button style={s.statusBtnQuiet} disabled={busy} onClick={() => window.confirm('Cancel this job?') && setStatus('cancelled')}>Cancel job</button>}
        {closed && <button style={s.statusBtn} disabled={busy} onClick={() => setStatus('in_progress')}>Reopen</button>}
        {job.status === 'done' && (
          <button style={s.statusBtn} disabled={busy} title="The work didn't hold: open a new job, flagged and linked to this one"
            onClick={() => ctx.setNewJob({
              returnOfJob: job, customerId: job.customerId, customerName: job.customerName, contactName: job.contactName || '', contactPhone: job.contactPhone || '',
              contactEmail: job.contactEmail || '', siteAddress: job.siteAddress || '', equipmentOwner: job.equipmentOwner || '', assetId: job.assetId || '',
              assetLabel: job.assetId ? '' : job.assetLabel || '', availability: job.availability || '', assignedTo: job.assignedTo || '',
            })}>↩ Return visit</button>
        )}
      </div>
      {error && <p style={s.err}>{error}</p>}

      {job.trackerNote && (
        <section style={s.section}>
          <h3 style={s.h3}>Office tracker note</h3>
          <div style={s.noteText}>{job.trackerNote}</div>
        </section>
      )}

      <section style={s.section}>
        <h3 style={s.h3}>Machine</h3>
        {asset
          ? <button style={s.linkish} onClick={() => ctx.openAsset(asset.assetId)}>{job.assetLabel}{asset.serial ? ` · serial ${asset.serial}` : ''}</button>
          : <div>{job.assetLabel || <span style={s.muted}>Not recorded</span>}</div>}
        {request && <div style={s.muted}>From a customer request sent {fmtWhen(request.createdAt)}{request.submittedByEmail ? ` by ${request.submittedByEmail}` : ''}. The customer sees this job's progress in the portal.</div>}
        {job.receivedVia && <div style={s.muted}>Came in by {RECEIVED_VIA[job.receivedVia].toLowerCase()}{job.createdByName ? `, logged by ${job.createdByName}` : ''}.</div>}
      </section>

      {returnVisits.length > 0 && (
        <section style={s.section}>
          <h3 style={{ ...s.h3, color: '#b3261e' }}>Return visits for this job <span style={s.count}>{returnVisits.length}</span></h3>
          {returnVisits.map((j) => <JobCard key={j.jobId} job={j} onOpen={ctx.openJob} showAssignee />)}
        </section>
      )}

      <section style={s.section}>
        <div style={s.formGrid}>
          <Field label="What's wrong / what needs doing"><textarea style={s.textarea} rows={3} value={draft.problem} onChange={(e) => set('problem', e.target.value)} /></Field>
          <div style={s.row2}>
            <Field label="Technician"><StaffSelect staff={ctx.staff} value={draft.assignedTo} onChange={(v) => set('assignedTo', v || '')} /></Field>
            <Field label="Date"><input style={s.input} type="date" value={draft.scheduledFor || ''} onChange={(e) => set('scheduledFor', e.target.value)} /></Field>
          </div>
          <div style={s.row2}>
            <Field label="Time of day"><input style={s.input} value={draft.scheduledWindow} onChange={(e) => set('scheduledWindow', e.target.value)} /></Field>
            <Field label="Type">
              <select style={s.select} value={draft.type} onChange={(e) => set('type', e.target.value)}>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>
          <div style={s.row2}>
            <Field label="Priority">
              <select style={s.select} value={draft.priority} onChange={(e) => set('priority', e.target.value)}>
                {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Request came in" hint={draft.requestedOn ? null : `Blank means the day the job was opened, ${fmtDay(job.createdAt ? new Date(job.createdAt).toLocaleDateString('en-CA') : '')}.`}>
              <input style={s.input} type="date" value={draft.requestedOn || ''} onChange={(e) => set('requestedOn', e.target.value)} />
            </Field>
          </div>
          <Field label="What was done" hint="Needed before the job can be closed as done.">
            <textarea style={s.textarea} rows={3} value={draft.resolution} onChange={(e) => set('resolution', e.target.value)} />
          </Field>
          <Field label="Parts used">
            {parts.map((p, i) => (
              <div key={i} style={s.partRow}>
                {p.partId
                  ? <div style={{ ...s.input, flex: 1, background: '#fafafa' }}>{p.name}<span style={s.hint}> · from stock</span></div>
                  : <input style={{ ...s.input, flex: 1 }} value={p.name} placeholder="Part (not in the parts list)" onChange={(e) => set('partsUsed', parts.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />}
                <input style={{ ...s.input, width: 70 }} type="number" min="1" value={p.qty} onChange={(e) => set('partsUsed', parts.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
                <button type="button" style={s.xBtn} onClick={() => set('partsUsed', parts.filter((_, j) => j !== i))} aria-label="Remove part">×</button>
              </div>
            ))}
            <select style={s.select} value="" onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              const stocked = ctx.parts.find((x) => x.partId === v);
              set('partsUsed', [...parts, stocked ? { partId: stocked.partId, name: stocked.name, qty: 1 } : { name: '', qty: 1 }]);
            }}>
              <option value="">+ Add a part…</option>
              {ctx.parts.filter((x) => x.active !== false).map((x) => <option key={x.partId} value={x.partId}>{x.name}{x.partNumber ? ` (${x.partNumber})` : ''} — {x.onHand} in stock</option>)}
              <option value="other">Something not in the parts list</option>
            </select>
          </Field>
          <div style={s.row2}>
            <Field label="Time spent (minutes)"><input style={s.input} type="number" min="0" step="5" value={draft.timeMinutes || ''} onChange={(e) => set('timeMinutes', e.target.value)} /></Field>
            <Field label="Signed off by (customer)"><input style={s.input} value={draft.signedBy} onChange={(e) => set('signedBy', e.target.value)} placeholder="Name of who checked the work" /></Field>
          </div>
          {billingSet ? (
            <div style={s.muted}>
              {job.invoice ? `Billed on QuickBooks invoice ${job.invoice.number}.` : 'The office marked this job no charge.'}
              {ctx.me?.canBill ? ' To change that, use the QuickBooks invoice box below.' : ' Only the office can change that.'}
            </div>
          ) : (
            <div>
              <label style={s.checkRow}><input type="checkbox" checked={!!draft.billable} onChange={(e) => set('billable', e.target.checked)} /> Bill the customer for this job</label>
              {draft.billable && <div style={s.hint}>The office links the QuickBooks invoice once it's billed.</div>}
            </div>
          )}
          <div style={s.row2}>
            <Field label="Contact name"><input style={s.input} value={draft.contactName} onChange={(e) => set('contactName', e.target.value)} /></Field>
            <Field label="Contact phone"><input style={s.input} type="tel" value={draft.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} /></Field>
          </div>
          <Field label="Contact email"><input style={s.input} type="email" value={draft.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} /></Field>
          <Field label="Whose machine"><Choice options={OWNER_LABELS} value={draft.equipmentOwner || ''} onChange={(v) => set('equipmentOwner', v)} /></Field>
          <Field label="Availability"><input style={s.input} value={draft.availability} onChange={(e) => set('availability', e.target.value)} placeholder="When the customer can have us" /></Field>
          <Field label="Site address"><input style={s.input} value={draft.siteAddress} onChange={(e) => set('siteAddress', e.target.value)} /></Field>
        </div>
        <div style={s.saveBar}>
          <button style={busy || !dirty ? { ...s.primary, ...s.off } : s.primary} disabled={busy || !dirty} onClick={() => save()}>{busy ? 'Saving…' : 'Save changes'}</button>
          {saved && !dirty && <span style={s.savedNote}>{saved}</span>}
        </div>
      </section>

      <JobActionItems job={job} onChange={setJob} />
      <JobFeedback job={job} contactEmail={job.contactEmail || (request && request.submittedByEmail) || ''} onChange={setJob} />
      <JobInvoice ctx={ctx} job={job} onChange={(next) => { setJob(next); setDraft((d) => ({ ...d, billable: next.billable })); }} />
      <JobChecklists ctx={ctx} job={job} onChange={setJob} />
      <JobFollowUps ctx={ctx} job={job} onChange={setJob} />

      <section style={s.section}>
        <h3 style={s.h3}>Photos</h3>
        <div style={s.photoGrid}>
          {(job.photos || []).map((p) => (
            <a key={p.key} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={`Taken ${fmtWhen(p.at)}`} style={s.photo} /></a>
          ))}
          <label style={s.photoAdd}>
            {busy ? 'Uploading…' : '+ Add photo'}
            <input type="file" accept="image/*" style={{ display: 'none' }} disabled={busy} onChange={(e) => { addPhoto(e.target.files && e.target.files[0]); e.target.value = ''; }} />
          </label>
        </div>
      </section>

      <section style={s.section}>
        <h3 style={s.h3}>Notes</h3>
        {(job.notes || []).length === 0 && <p style={s.muted}>No notes yet.</p>}
        {[...(job.notes || [])].reverse().map((n, i) => (
          <div key={i} style={s.note}>
            <div style={s.noteMeta}>{n.byName || n.by} · {fmtWhen(n.at)}</div>
            <div style={s.noteText}>{n.text}</div>
          </div>
        ))}
        <textarea style={s.textarea} rows={2} placeholder="Add a note — what you found, what you ordered, what to check next time" value={note} onChange={(e) => setNote(e.target.value)} />
        <button style={s.outlineSm} disabled={busy || !note.trim()} onClick={addNote}>Add note</button>
      </section>

      <p style={s.muted}>Opened {fmtWhen(job.createdAt)}{job.createdByName ? ` by ${job.createdByName}` : ''} · last change {fmtWhen(job.updatedAt)}</p>
    </div>
  );
}

// The job's QuickBooks invoice. The office links it here or on the Tracker;
// everyone else sees whether it has been billed.
function JobInvoice({ ctx, job, onChange }) {
  const [editing, setEditing] = useState(false);
  const canBill = !!ctx.me?.canBill;
  const inv = job.invoice;
  const asOf = inv && balanceDay({ balanceAt: inv.balanceAt || job.billingSetAt });
  const billed = inv ? 'invoice' : job.billingStatus === 'no_charge' ? 'no_charge' : null;
  const done = job.status === 'done';
  if (!canBill && !billed && !done) return null;
  return (
    <section style={s.section}>
      <h3 style={s.h3}>QuickBooks invoice</h3>
      {inv ? (
        <div>
          <a href={inv.url} target="_blank" rel="noreferrer">Invoice {inv.number}</a>
          <span style={s.trNote}>
            {' '}· {money(inv.total)}{inv.date ? ` · ${fmtShortDay(inv.date, '')}` : ''}
            {' '}· {inv.balance > 0 ? `${money(inv.balance)} still owed${asOf ? ` as of ${fmtShortDay(asOf, '')}` : ''}` : 'paid'}
          </span>
        </div>
      ) : billed === 'no_charge' ? <div style={s.muted}>No charge: nothing to invoice.</div>
        : <div style={done ? s.gapText : s.muted}>{done ? 'Not invoiced yet.' : 'Not invoiced. Link the invoice once the work is billed.'}</div>}
      {canBill && (editing
        ? <div style={{ marginTop: 8 }}><InvoiceLinker jobId={job.jobId} done={done} billed={billed} onDone={(next) => { onChange(next); setEditing(false); }} onCancel={() => setEditing(false)} /></div>
        : <button style={{ ...s.outlineSm, marginTop: 8 }} onClick={() => setEditing(true)}>{billed ? 'Change' : 'Add invoice'}</button>)}
    </section>
  );
}

// ── On a job: action items for the office ──
// Parts to order, something to tell the customer. Each is emailed to Olaf and
// Andrea when it's added (also when a checklist's "action needed" step is
// filled in); the office ticks it off once it's dealt with.
function JobActionItems({ job, onChange }) {
  const [form, setForm] = useState({ kind: 'parts', text: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  async function run(fn) {
    setBusy(true); setError(''); setNote('');
    try { const r = await fn(); onChange(r.job); return r; } catch (e) { setError(e.message); return null; } finally { setBusy(false); }
  }
  async function add(e) {
    e.preventDefault();
    const r = await run(() => serviceApi.addActionItem(job.jobId, form));
    if (!r) return;
    setForm((f) => ({ ...f, text: '' }));
    setNote(r.emailed ? 'Emailed to the office.' : "Saved, but the email to the office didn't go. Tell them.");
  }
  const list = [...(job.actionItems || [])].sort((a, b) => (a.done - b.done) || String(a.at).localeCompare(String(b.at)));
  return (
    <section style={s.section}>
      <h3 style={s.h3}>Action items for the office</h3>
      <p style={s.muted}>Parts to order, or anything the office needs to tell the customer. Emailed to Olaf and Andrea as soon as you add it.</p>
      {list.map((a) => (
        <label key={a.id} style={{ ...s.checkRow, alignItems: 'flex-start', opacity: a.done ? 0.55 : 1 }}>
          <input type="checkbox" checked={!!a.done} disabled={busy} onChange={(e) => run(() => serviceApi.setActionItemDone(job.jobId, a.id, e.target.checked))} style={{ marginTop: 4 }} />
          <span>
            <strong style={{ color: a.done ? '#555' : '#b3261e' }}>{ACTION_KINDS[a.kind] || ACTION_KINDS.other}</strong> — {a.step ? `${a.step.label}: ` : ''}{a.text}
            <span style={s.hint}> · {a.byName || a.by} · {fmtWhen(a.at)}{a.emailedAt ? ' · emailed' : ' · not emailed'}{a.done ? ` · dealt with by ${a.doneByName || a.doneBy}` : ''}</span>
          </span>
        </label>
      ))}
      <form onSubmit={add} style={s.inlineForm}>
        <select style={s.select} value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))} aria-label="Kind of action item">
          {Object.entries(ACTION_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input style={{ ...s.input, flex: '1 1 240px' }} placeholder="e.g. order a group head gasket, or tell them to descale weekly" value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} />
        <button style={s.outlineSm} type="submit" disabled={busy || !form.text.trim()}>{busy ? 'Sending…' : 'Send to the office'}</button>
      </form>
      {note && <p style={s.savedNote}>{note}</p>}
      {error && <p style={s.err}>{error}</p>}
    </section>
  );
}

// ── On a completed job: the customer's feedback ──
function JobFeedback({ job, contactEmail, onChange }) {
  const asked = job.feedbackRequests || [];
  const last = asked[asked.length - 1];
  const [to, setTo] = useState(contactEmail || (last && last.to) || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (job.status !== 'done') return null;
  const fb = job.feedback;
  async function send(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try { onChange((await serviceApi.askForFeedback(job.jobId, to.trim())).job); } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  return (
    <section style={s.section}>
      <h3 style={s.h3}>Customer feedback</h3>
      {fb && (
        <div style={{ ...s.note, borderLeft: `4px solid ${fb.satisfied ? '#1a7f37' : '#b3261e'}` }}>
          <div style={{ fontWeight: 700, color: fb.satisfied ? '#1a7f37' : '#b3261e' }}>{fb.satisfied ? 'Happy with the repair' : 'Not happy with the repair'}</div>
          {fb.questions ? <div style={s.noteText}>{fb.questions}</div> : <div style={s.muted}>No questions.</div>}
          <div style={s.noteMeta}>{[fb.name, fb.to].filter(Boolean).join(' · ')} · {fmtWhen(fb.at)}</div>
        </div>
      )}
      {(job.feedbackHistory || []).length > 0 && (
        <p style={s.muted}>Earlier answers: {[...job.feedbackHistory].reverse().map((h) => `${h.satisfied ? 'happy' : 'not happy'}${h.questions ? ` ("${h.questions}")` : ''} on ${fmtWhen(h.at)}`).join('; ')}.</p>
      )}
      {last && <p style={s.muted}>Feedback form sent to {last.to} {fmtWhen(last.at)}{last.byName ? ` by ${last.byName}` : ''}{asked.length > 1 ? ` (${asked.length} times)` : ''}.{!fb ? ' No answer yet.' : ''}</p>}
      {!last && <p style={s.muted}>Email the customer a short form: are they happy with the work, and any questions about it. Their answer comes back to Olaf and Andrea.</p>}
      <form onSubmit={send} style={s.inlineForm}>
        <input style={{ ...s.input, flex: '1 1 240px' }} type="email" placeholder="Customer's email" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Customer's email" />
        <button style={last ? s.outlineSm : s.primarySm} type="submit" disabled={busy || !to.trim()}>{busy ? 'Sending…' : last ? 'Send again' : 'Send feedback form'}</button>
      </form>
      {error && <p style={s.err}>{error}</p>}
    </section>
  );
}

// ── Requests customers send from the ordering portal ──
// Shown at the top of Requested until each is made a job (which then sits in
// Requested or Scheduled like any other) or closed.
function WebsiteRequests({ ctx }) {
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState('');
  const [making, setMaking] = useState(null);   // requestId being turned into a job
  const [assign, setAssign] = useState({ assignedTo: '', scheduledFor: '' });
  const [busy, setBusy] = useState(false);
  const { refreshCounts } = ctx;

  const load = useCallback(() => serviceApi.requests().then((r) => setRequests(r.requests)).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);

  async function makeJob(r) {
    setBusy(true); setError('');
    try { const { job } = await serviceApi.requestToJob(r.requestId, assign); ctx.openJob(job.jobId); }
    catch (e) { setError(e.message); setBusy(false); }
  }
  async function close(r) {
    if (!window.confirm(`Close ${r.customerName || 'this'}'s request without a job? The customer will see it as resolved.`)) return;
    setBusy(true);
    try { await serviceApi.closeRequest(r.requestId); load(); refreshCounts(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  if (!requests) return error ? <p style={s.err}>{error}</p> : null;
  const waiting = requests.filter((r) => !r.jobId);
  if (!waiting.length) return null;
  return (
    <section style={s.section}>
      <h3 style={s.h3}>From the website <span style={s.count}>{waiting.length}</span></h3>
      <p style={s.muted}>Sent from the ordering portal's service form. Make a job for each one, or close it if nothing's needed.</p>
      {error && <p style={s.err}>{error}</p>}
      {waiting.map((r) => {
        const f = r.fields || {};
        const urgent = String(f.urgency || '').toLowerCase().startsWith('asap');
        return (
          <div key={r.requestId} style={{ ...s.card, cursor: 'default', borderLeft: urgent ? '4px solid #b3261e' : s.card.borderLeft }}>
            <div style={s.cardTop}>
              {urgent && <Pill meta={PRIORITY_META.urgent} />}
              <span style={s.when}>{fmtWhen(r.createdAt)}</span>
            </div>
            <div style={s.cardTitle}>{r.customerName}</div>
            <div style={s.cardMachine}>{f.equipment}</div>
            <div style={s.cardProblem}>{f.issue}</div>
            {f.additionalNotes && <div style={s.muted}>{f.additionalNotes}</div>}
            <div style={s.cardFoot}>
              {f.urgency && <span>{f.urgency}</span>}
              {f.preferredDate && <span>Would like: {f.preferredDate}</span>}
              {f.contactPhone && <a href={telUrl(f.contactPhone)} onClick={(e) => e.stopPropagation()}>{r.submittedByName || 'Call'} · {f.contactPhone}</a>}
            </div>
            {making === r.requestId ? (
              <div style={s.inlineForm}>
                <StaffSelect staff={ctx.staff} value={assign.assignedTo} onChange={(v) => setAssign((a) => ({ ...a, assignedTo: v || '' }))} />
                <input style={s.input} type="date" value={assign.scheduledFor} onChange={(e) => setAssign((a) => ({ ...a, scheduledFor: e.target.value }))} />
                <button style={s.primarySm} disabled={busy} onClick={() => makeJob(r)}>Create job</button>
                <button style={s.outlineSm} onClick={() => setMaking(null)}>Cancel</button>
              </div>
            ) : (
              <div style={s.inlineForm}>
                <button style={s.primarySm} onClick={() => { setMaking(r.requestId); setAssign({ assignedTo: '', scheduledFor: '' }); }}>Make a job</button>
                <button style={s.outlineSm} disabled={busy} onClick={() => close(r)}>Close — no job needed</button>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

// ── Equipment ──
function Equipment({ ctx }) {
  const [assets, setAssets] = useState(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { serviceApi.assets().then((r) => setAssets(r.assets)).catch((e) => setError(e.message)); }, [ctx.refreshKey]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (assets || []).filter((a) => !needle || [a.customerName, a.name, a.make, a.model, a.serial, a.location].some((v) => String(v || '').toLowerCase().includes(needle)));
  }, [assets, q]);
  return (
    <div>
      <div style={s.titleRow}>
        <h2 style={s.h2}>Equipment</h2>
        <button style={s.primarySm} onClick={() => ctx.setNewAsset({})}>+ Add machine</button>
      </div>
      <input style={{ ...s.input, width: '100%', marginBottom: 12 }} placeholder="Search customer, make, model, serial…" value={q} onChange={(e) => setQ(e.target.value)} />
      {error && <p style={s.err}>{error}</p>}
      {!assets ? <p style={s.muted}>Loading…</p> : filtered.length === 0 ? (
        <div style={s.empty}>{assets.length === 0 ? 'No machines recorded yet. Add the machines at each customer as you visit them.' : 'No machines match.'}</div>
      ) : filtered.map((a) => (
        <div key={a.assetId} style={{ ...s.card, opacity: a.active === false ? 0.55 : 1 }} onClick={() => ctx.openAsset(a.assetId)} role="button" tabIndex={0}>
          <div style={s.cardTitle}>{a.name || [a.make, a.model].filter(Boolean).join(' ')}</div>
          <div style={s.cardMachine}>{a.customerName}{a.location ? ` · ${a.location}` : ''}</div>
          <div style={s.cardFoot}>
            {a.make && a.name && <span>{[a.make, a.model].filter(Boolean).join(' ')}</span>}
            {a.serial && <span>Serial {a.serial}</span>}
            <span>{OWNERSHIP_LABELS[a.ownership] || ''}</span>
            {a.active === false && <span>No longer there</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

const ASSET_KEYS = ['name', 'make', 'model', 'serial', 'location', 'ownership', 'installedAt', 'warrantyUntil', 'pmIntervalDays', 'notes', 'active'];

function AssetFields({ form, set }) {
  return (
    <div style={s.formGrid}>
      <Field label="Name" hint="What the team calls it, e.g. 'Lobby espresso machine'"><input style={s.input} value={form.name || ''} onChange={(e) => set('name', e.target.value)} /></Field>
      <div style={s.row2}>
        <Field label="Make"><input style={s.input} value={form.make || ''} onChange={(e) => set('make', e.target.value)} placeholder="La Marzocco" /></Field>
        <Field label="Model"><input style={s.input} value={form.model || ''} onChange={(e) => set('model', e.target.value)} placeholder="Linea PB 2-group" /></Field>
      </div>
      <div style={s.row2}>
        <Field label="Serial number"><input style={s.input} value={form.serial || ''} onChange={(e) => set('serial', e.target.value)} /></Field>
        <Field label="Where in the venue"><input style={s.input} value={form.location || ''} onChange={(e) => set('location', e.target.value)} placeholder="Lobby bar" /></Field>
      </div>
      <div style={s.row2}>
        <Field label="Whose machine">
          <select style={s.select} value={form.ownership || 'unknown'} onChange={(e) => set('ownership', e.target.value)}>
            {Object.entries(OWNERSHIP_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Service every (days)" hint="For scheduled maintenance — coming next"><input style={s.input} type="number" min="0" value={form.pmIntervalDays || ''} onChange={(e) => set('pmIntervalDays', e.target.value)} /></Field>
      </div>
      <div style={s.row2}>
        <Field label="Installed"><input style={s.input} type="date" value={form.installedAt || ''} onChange={(e) => set('installedAt', e.target.value)} /></Field>
        <Field label="Warranty until"><input style={s.input} type="date" value={form.warrantyUntil || ''} onChange={(e) => set('warrantyUntil', e.target.value)} /></Field>
      </div>
      <Field label="Notes"><textarea style={s.textarea} rows={3} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} /></Field>
    </div>
  );
}

function AssetForm({ ctx, prefill }) {
  const [customer, setCustomer] = useState(() => ctx.customers.find((c) => c.id === prefill.customerId) || null);
  const [form, setForm] = useState({ ownership: 'unknown', ...prefill });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  async function submit(e) {
    e.preventDefault();
    if (!customer) { setError('Pick a customer from the list.'); return; }
    setBusy(true); setError('');
    try { const { asset } = await serviceApi.createAsset({ ...form, customerId: customer.id, customerName: customer.name }); ctx.openAsset(asset.assetId); }
    catch (err) { setError(err.message); setBusy(false); }
  }
  return (
    <form onSubmit={submit}>
      <button type="button" style={s.backBtn} onClick={ctx.back}>← Back</button>
      <h2 style={s.h2}>Add a machine</h2>
      <div style={s.formGrid}><Field label="Customer"><CustomerPicker customers={ctx.customers} value={customer?.id} onChange={setCustomer} /></Field></div>
      <AssetFields form={form} set={set} />
      {error && <p style={s.err}>{error}</p>}
      <button style={s.primary} type="submit" disabled={busy}>{busy ? 'Saving…' : 'Add machine'}</button>
    </form>
  );
}

function AssetDetail({ ctx, assetId }) {
  const [asset, setAsset] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  useEffect(() => {
    serviceApi.asset(assetId).then((r) => { setAsset(r.asset); setForm(r.asset); setJobs(r.jobs); }).catch((e) => setError(e.message));
  }, [assetId]);
  if (!asset || !form) return error ? <p style={s.err}>{error}</p> : <p style={s.muted}>Loading machine…</p>;
  const set = (k, v) => { setSaved(''); setForm((f) => ({ ...f, [k]: v })); };
  const dirty = ASSET_KEYS.some((k) => (form[k] ?? '') !== (asset[k] ?? ''));
  async function save() {
    setBusy(true); setError('');
    try {
      const patch = Object.fromEntries(ASSET_KEYS.filter((k) => (form[k] ?? '') !== (asset[k] ?? '')).map((k) => [k, form[k]]));
      const { asset: next } = await serviceApi.updateAsset(asset.assetId, patch);
      setAsset(next); setForm(next); setSaved('Saved');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  return (
    <div>
      <button style={s.backBtn} onClick={ctx.back}>← Back</button>
      <h2 style={s.h2}>{asset.name || [asset.make, asset.model].filter(Boolean).join(' ')}</h2>
      <div style={s.muted}>{asset.customerName}</div>
      <div style={s.contactRow}>
        <button style={s.primarySm} onClick={() => ctx.setNewJob({ customerId: asset.customerId, assetId: asset.assetId })}>+ New job for this machine</button>
      </div>
      <section style={s.section}>
        <h3 style={s.h3}>Job history <span style={s.count}>{jobs.length}</span></h3>
        {jobs.length === 0 ? <p style={s.muted}>No jobs on this machine yet.</p> : jobs.map((j) => <JobCard key={j.jobId} job={j} onOpen={ctx.openJob} showAssignee />)}
      </section>
      <section style={s.section}>
        <h3 style={s.h3}>Details</h3>
        <AssetFields form={form} set={set} />
        <label style={s.checkRow}><input type="checkbox" checked={form.active !== false} onChange={(e) => set('active', e.target.checked)} /> Still at this customer</label>
        <div style={s.saveBar}>
          <button style={busy || !dirty ? { ...s.primary, ...s.off } : s.primary} disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button>
          {saved && !dirty && <span style={s.savedNote}>{saved}</span>}
        </div>
        {error && <p style={s.err}>{error}</p>}
      </section>
    </div>
  );
}

// ── On a job: checklists ──
function JobChecklists({ ctx, job, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sentNote, setSentNote] = useState('');
  const attached = job.checklists || [];
  const available = ctx.checklists.filter((t) => t.active !== false && !attached.some((c) => c.templateId === t.templateId));
  // `emailed` comes back when an action step was just filled in.
  async function run(fn) {
    setBusy(true); setError('');
    try {
      const { job: next, emailed } = await fn();
      onChange(next);
      if (emailed !== undefined) setSentNote(emailed ? 'Sent to the office as an action item.' : "Saved as an action item, but the email to the office didn't go. Tell them.");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  const setItem = (c, it, value) => run(() => serviceApi.setChecklistItem(job.jobId, c.templateId, it.id, value));
  const addPicture = (c, it, image) => run(() => serviceApi.addChecklistPhoto(job.jobId, c.templateId, it.id, image));
  async function addPhotoFile(c, it, file) {
    if (!file) return;
    try { addPicture(c, it, await resizeToJpeg(await readFileAsDataUrl(file), 1600, 0.75)); } catch (e) { setError(e.message); }
  }
  return (
    <section style={s.section}>
      <h3 style={s.h3}>Checklists</h3>
      {attached.map((c) => {
        const steps = c.items.filter((it) => it.kind !== 'heading');
        const done = steps.filter(stepAnswered).length;
        return (
          <div key={c.templateId} style={s.checklistBox}>
            <div style={s.checklistHead}>
              <strong>{c.title}</strong>
              <span style={s.count}>{done} of {steps.length}{c.source === 'maintainx' ? ' · filled in MaintainX' : ''}</span>
            </div>
            {c.items.map((it) => (it.kind === 'heading' ? <div key={it.id} style={s.clHeading}>{it.label}</div> : (
              <div key={it.id} style={s.checkRowItem}>
                <ChecklistStep it={it} busy={busy} onValue={(v) => setItem(c, it, v)} onPhoto={(file) => addPhotoFile(c, it, file)} onSign={(png) => addPicture(c, it, png)} />
                {isActionStep(it) && <div style={s.hint}>Filled in, this is emailed to the office as an action item.</div>}
                <StepPictures photos={it.photos} />
                {it.by && <div style={s.hint}>{it.by}{it.at ? ` · ${new Date(it.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}</div>}
              </div>
            )))}
          </div>
        );
      })}
      {sentNote && <p style={s.savedNote}>{sentNote}</p>}
      {attached.length === 0 && <p style={s.muted}>No checklist on this job.</p>}
      {available.length > 0 && (
        <select style={s.select} value="" disabled={busy} onChange={(e) => e.target.value && run(() => serviceApi.attachChecklist(job.jobId, e.target.value))}>
          <option value="">+ Add a checklist…</option>
          {available.map((t) => <option key={t.templateId} value={t.templateId}>{t.title}</option>)}
        </select>
      )}
      {error && <p style={s.err}>{error}</p>}
    </section>
  );
}

const stepAnswered = (it) => (it.value !== null && it.value !== undefined && it.value !== false && it.value !== '') || (it.photos || []).length > 0;

// One checklist step, by kind. Text and numbers save when you leave the box.
function ChecklistStep({ it, busy, onValue, onPhoto, onSign }) {
  const [signing, setSigning] = useState(false);
  if (it.kind === 'check') {
    return <label style={s.checkRow}><input type="checkbox" checked={it.value === true} disabled={busy} onChange={(e) => onValue(e.target.checked)} /> {it.label}</label>;
  }
  if (it.kind === 'yes_no_na') {
    return (
      <div style={s.field}>
        <span style={s.fieldLabel}>{it.label}</span>
        <div style={s.segRow}>
          {['Yes', 'No', 'N/A'].map((o) => (
            <button key={o} type="button" disabled={busy} style={it.value === o ? { ...s.seg, ...s.segOn } : s.seg} onClick={() => onValue(it.value === o ? '' : o)}>{o}</button>
          ))}
        </div>
      </div>
    );
  }
  if (it.kind === 'choice') {
    return (
      <label style={s.field}>
        <span style={s.fieldLabel}>{it.label}</span>
        <select style={s.select} value={it.value || ''} disabled={busy} onChange={(e) => onValue(e.target.value)}>
          <option value="">—</option>
          {(it.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  if (it.kind === 'photo') {
    return (
      <div style={s.field}>
        <span style={s.fieldLabel}>{it.label}</span>
        <label style={{ ...s.outlineSm, alignSelf: 'flex-start' }}>
          {busy ? 'Uploading…' : '+ Photo'}
          <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} disabled={busy} onChange={(e) => { onPhoto(e.target.files && e.target.files[0]); e.target.value = ''; }} />
        </label>
      </div>
    );
  }
  if (it.kind === 'signature') {
    return (
      <div style={s.field}>
        <span style={s.fieldLabel}>{it.label}</span>
        <input style={s.input} placeholder="Name of who signed" defaultValue={it.value ?? ''} disabled={busy}
          onBlur={(e) => { if (String(e.target.value) !== String(it.value ?? '')) onValue(e.target.value); }} />
        {signing
          ? <SignaturePad busy={busy} onCancel={() => setSigning(false)} onSave={(png) => { setSigning(false); onSign(png); }} />
          : <button type="button" style={{ ...s.outlineSm, alignSelf: 'flex-start' }} disabled={busy} onClick={() => setSigning(true)}>{(it.photos || []).length ? 'Sign again' : 'Sign on screen'}</button>}
      </div>
    );
  }
  return (
    <label style={s.field}>
      <span style={s.fieldLabel}>{it.label}</span>
      <input style={s.input} type={it.kind === 'number' ? 'number' : 'text'} defaultValue={it.value ?? ''} disabled={busy}
        onBlur={(e) => { if (String(e.target.value) !== String(it.value ?? '')) onValue(e.target.value); }} />
    </label>
  );
}

// Pictures on a step. Phones' HEIC photos and videos open in a new tab, as
// not every browser can show them in the page.
function StepPictures({ photos }) {
  if (!(photos || []).length) return null;
  return (
    <div style={{ ...s.photoGrid, marginTop: 6 }}>
      {photos.map((p) => {
        const ext = String(p.key).split('.').pop().toLowerCase();
        if (['mp4', 'mov', 'heic', 'bin', 'pdf'].includes(ext)) {
          return <a key={p.key} href={p.url} target="_blank" rel="noreferrer" style={s.fileTile}>{ext === 'mp4' || ext === 'mov' ? '▶ Video' : ext === 'heic' ? 'Photo (HEIC)' : 'File'}</a>;
        }
        return <a key={p.key} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={p.name || 'Step photo'} style={ext === 'png' ? s.signatureImg : s.photo} /></a>;
      })}
    </div>
  );
}

// Sign with a finger or the mouse; saved as a PNG.
function SignaturePad({ busy, onSave, onCancel }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);
  useEffect(() => {
    const c = ref.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio; c.height = c.offsetHeight * ratio;
    const g = c.getContext('2d');
    g.scale(ratio, ratio); g.lineWidth = 2.2; g.lineCap = 'round'; g.lineJoin = 'round'; g.strokeStyle = '#111';
    g.fillStyle = '#fff'; g.fillRect(0, 0, c.offsetWidth, c.offsetHeight);
  }, []);
  const at = (e) => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); ref.current.setPointerCapture(e.pointerId); drawing.current = true; const g = ref.current.getContext('2d'); g.beginPath(); g.moveTo(...at(e)); };
  const move = (e) => { if (!drawing.current) return; const g = ref.current.getContext('2d'); g.lineTo(...at(e)); g.stroke(); setEmpty(false); };
  const up = () => { drawing.current = false; };
  const clear = () => { const c = ref.current; const g = c.getContext('2d'); g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height); setEmpty(true); };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <canvas ref={ref} style={s.sigCanvas} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" style={s.primary} disabled={busy || empty} onClick={() => onSave(ref.current.toDataURL('image/png'))}>Save signature</button>
        <button type="button" style={s.outlineSm} onClick={clear}>Clear</button>
        <button type="button" style={s.outlineSm} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── On a job: follow-ups ──
function JobFollowUps({ ctx, job, onChange }) {
  const [form, setForm] = useState({ dueDate: '', note: '', assignedTo: ctx.me?.email || '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function run(fn) {
    setBusy(true); setError('');
    try { const { job: next } = await fn(); onChange(next); return true; } catch (e) { setError(e.message); return false; } finally { setBusy(false); }
  }
  async function add(e) {
    e.preventDefault();
    if (await run(() => serviceApi.addFollowUp(job.jobId, form))) setForm((f) => ({ ...f, dueDate: '', note: '' }));
  }
  const list = [...(job.followUps || [])].sort((a, b) => (a.done - b.done) || a.dueDate.localeCompare(b.dueDate));
  return (
    <section style={s.section}>
      <h3 style={s.h3}>Follow-ups</h3>
      {list.map((f) => (
        <label key={f.id} style={{ ...s.checkRow, opacity: f.done ? 0.55 : 1 }}>
          <input type="checkbox" checked={!!f.done} disabled={busy} onChange={(e) => run(() => serviceApi.setFollowUpDone(job.jobId, f.id, e.target.checked))} />
          <span><strong>{fmtDay(f.dueDate)}</strong> — {f.note}{f.assignedName ? <span style={s.hint}> · {f.assignedName}</span> : null}</span>
        </label>
      ))}
      <form onSubmit={add} style={s.inlineForm}>
        <input style={s.input} type="date" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} aria-label="Follow-up date" />
        <input style={{ ...s.input, flex: '1 1 200px' }} placeholder="e.g. call to check the steam wand is OK" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
        <StaffSelect staff={ctx.staff} value={form.assignedTo} onChange={(v) => setForm((f) => ({ ...f, assignedTo: v || '' }))} />
        <button style={s.outlineSm} type="submit" disabled={busy || !form.dueDate || !form.note.trim()}>Add follow-up</button>
      </form>
      {error && <p style={s.err}>{error}</p>}
    </section>
  );
}

// ── Maintenance schedules ──
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
function Schedules({ ctx }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const load = useCallback(() => serviceApi.schedules().then((r) => setList(r.schedules)).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  async function runNow() {
    setNote('');
    try { const r = await serviceApi.runSchedules(); setNote(r.created ? `Created ${r.created} job${r.created === 1 ? '' : 's'}.` : 'Nothing due yet.'); load(); }
    catch (e) { setError(e.message); }
  }
  if (editing) return <ScheduleForm ctx={ctx} schedule={editing === 'new' ? null : editing} onDone={() => { setEditing(null); load(); }} />;
  return (
    <div>
      <div style={s.titleRow}>
        <h2 style={s.h2}>Maintenance schedules</h2>
        <button style={s.primarySm} onClick={() => setEditing('new')}>+ New schedule</button>
      </div>
      <p style={s.muted}>Regular visits — monthly PMs, weekly refills. Each one puts its next job on the board a few days ahead, assigned and dated.</p>
      {error && <p style={s.err}>{error}</p>}
      {!list ? <p style={s.muted}>Loading…</p> : list.length === 0 ? <div style={s.empty}>No schedules yet.</div> : list.map((x) => (
        <div key={x.scheduleId} style={{ ...s.card, opacity: x.active === false ? 0.55 : 1 }} role="button" tabIndex={0} onClick={() => setEditing(x)}>
          <div style={s.cardTop}>
            <span style={s.jobNo}>{x.describe}</span>
            {x.active === false && <Pill meta={{ label: 'Paused', color: '#666', bg: '#f0f0f0' }} />}
            <span style={s.when}>Next: {fmtDay(x.nextDue)}</span>
          </div>
          <div style={s.cardTitle}>{x.title}</div>
          <div style={s.cardMachine}>{x.customerName}{x.assetLabel ? ` · ${x.assetLabel}` : ''}</div>
          {(x.planFee || x.billing) && <div style={s.cardMachine}>{[x.planFee, PLAN_BILLING[x.billing]].filter(Boolean).join(' · ')}</div>}
          {x.reviewNote && <div style={s.reviewNote}>To check: {x.reviewNote}</div>}
          <div style={s.cardFoot}><span>{TYPE_LABELS[x.type]}</span><span>{x.assignedName || 'Unassigned'}</span><span>Job appears {x.leadDays} days ahead</span></div>
        </div>
      ))}
      <p style={s.muted}>Jobs are created automatically when anyone opens the service desk. <button style={s.linkish} onClick={runNow}>Check now</button> {note}</p>
    </div>
  );
}

const PLAN_BILLING = { bill: 'Bill each visit', no_charge: 'No charge' };
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DEFAULT_SEASONS = [{ fromMonth: 11, toMonth: 3, every: 6, unit: 'weeks' }, { fromMonth: 4, toMonth: 10, every: 3, unit: 'months' }];

// Two seasons, each a run of months with its own gap between visits. The
// server checks they cover the year.
function SeasonsField({ seasons, onChange }) {
  const setSeason = (i, k, v) => onChange(seasons.map((x, j) => (j === i ? { ...x, [k]: k === 'unit' ? v : Number(v) } : x)));
  const month = (i, k) => (
    <select style={s.select} value={seasons[i][k]} onChange={(e) => setSeason(i, k, e.target.value)}>
      {MONTH_NAMES.map((m, n) => <option key={m} value={n + 1}>{m}</option>)}
    </select>
  );
  return (
    <Field label="Seasons" hint="together they must cover the whole year">
      {seasons.map((x, i) => (
        <div key={i} style={s.seasonRow}>
          {month(i, 'fromMonth')}<span>to</span>{month(i, 'toMonth')}<span>every</span>
          <input style={{ ...s.input, width: 64 }} type="number" min="1" max="52" value={x.every} onChange={(e) => setSeason(i, 'every', e.target.value)} />
          <select style={s.select} value={x.unit} onChange={(e) => setSeason(i, 'unit', e.target.value)}><option value="weeks">weeks</option><option value="months">months</option></select>
        </div>
      ))}
    </Field>
  );
}

function ScheduleForm({ ctx, schedule, onDone }) {
  const r = (schedule && schedule.recurrence) || { kind: 'monthly_weekday', weekday: 'Monday', weekOfMonth: '1', interval: 1 };
  const [customer, setCustomer] = useState(() => (schedule ? ctx.customers.find((c) => c.id === schedule.customerId) || { id: schedule.customerId, name: schedule.customerName } : null));
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({
    title: '', type: 'maintenance', priority: 'normal', assignedTo: '', assetId: '', problem: '', checklistId: '', leadDays: 7, startDay: '', active: true,
    planFee: '', billing: '', reviewNote: '',
    ...(schedule || {}), recurrence: { interval: 1, ...r },
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRec = (k, v) => setForm((f) => ({ ...f, recurrence: { ...f.recurrence, [k]: v } }));
  useEffect(() => { if (customer) serviceApi.assets({ customerId: customer.id }).then((x) => setAssets(x.assets)).catch(() => setAssets([])); }, [customer]);
  async function save(e) {
    e.preventDefault();
    if (!customer) { setError('Pick a customer from the list.'); return; }
    setBusy(true); setError('');
    const body = { ...form, customerId: customer.id, customerName: customer.name, assignedTo: form.assignedTo || null, assetId: form.assetId || null, checklistId: form.checklistId || null };
    if (schedule && JSON.stringify(schedule.recurrence) === JSON.stringify(form.recurrence)) delete body.recurrence;
    if (schedule && schedule.startDay === form.startDay) delete body.startDay;
    try { await serviceApi.saveSchedule(schedule && schedule.scheduleId, body); onDone(); }
    catch (err) { setError(err.message); setBusy(false); }
  }
  const rec = form.recurrence;
  return (
    <form onSubmit={save}>
      <button type="button" style={s.backBtn} onClick={onDone}>← Back</button>
      <h2 style={s.h2}>{schedule ? 'Edit schedule' : 'New schedule'}</h2>
      <div style={s.formGrid}>
        <Field label="Customer"><CustomerPicker customers={ctx.customers} value={customer?.id} onChange={setCustomer} /></Field>
        <Field label="Name" hint='e.g. "Monthly PM" or "Weekly HLF refill"'><input style={s.input} value={form.title} onChange={(e) => set('title', e.target.value)} /></Field>
        <Field label="Machine">
          <select style={s.select} value={form.assetId || ''} onChange={(e) => set('assetId', e.target.value)}>
            <option value="">Not one machine in particular</option>
            {assets.map((a) => <option key={a.assetId} value={a.assetId}>{a.name || [a.make, a.model].filter(Boolean).join(' ')}</option>)}
          </select>
        </Field>
        <QuickAddMachine customer={customer} onAdded={(a) => { setAssets((list) => [...list, a]); set('assetId', a.assetId); }} />
        <div style={s.row2}>
          <Field label="How often">
            <select style={s.select} value={rec.kind} onChange={(e) => { const kind = e.target.value; setForm((f) => ({ ...f, recurrence: { ...f.recurrence, kind, ...(kind === 'seasonal' && !f.recurrence.seasons ? { seasons: DEFAULT_SEASONS } : {}) } })); }}>
              <option value="weekly">Every week (or every few weeks)</option>
              <option value="monthly_weekday">A weekday each month, e.g. first Monday</option>
              <option value="monthly_date">A date each month, e.g. the 15th</option>
              <option value="every_days">Every so many days</option>
              <option value="seasonal">By season, e.g. every 6 weeks in winter</option>
            </select>
          </Field>
          {rec.kind === 'seasonal' ? null : rec.kind === 'every_days'
            ? <Field label="Days apart"><input style={s.input} type="number" min="1" value={rec.days || ''} onChange={(e) => setRec('days', e.target.value)} /></Field>
            : <Field label={rec.kind === 'weekly' ? 'Every how many weeks' : 'Every how many months'}><input style={s.input} type="number" min="1" value={rec.interval || 1} onChange={(e) => setRec('interval', e.target.value)} /></Field>}
        </div>
        {rec.kind === 'monthly_weekday' && (
          <div style={s.row2}>
            <Field label="Which week"><select style={s.select} value={rec.weekOfMonth || '1'} onChange={(e) => setRec('weekOfMonth', e.target.value)}>
              {[['1', 'First'], ['2', 'Second'], ['3', 'Third'], ['4', 'Fourth'], ['last', 'Last']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></Field>
            <Field label="Day"><select style={s.select} value={rec.weekday || 'Monday'} onChange={(e) => setRec('weekday', e.target.value)}>{WEEKDAY_NAMES.map((d) => <option key={d}>{d}</option>)}</select></Field>
          </div>
        )}
        {rec.kind === 'weekly' && <Field label="Day"><select style={s.select} value={rec.weekday || 'Monday'} onChange={(e) => setRec('weekday', e.target.value)}>{WEEKDAY_NAMES.map((d) => <option key={d}>{d}</option>)}</select></Field>}
        {rec.kind === 'monthly_date' && <Field label="Day of the month (1–28)"><input style={s.input} type="number" min="1" max="28" value={rec.day || ''} onChange={(e) => setRec('day', e.target.value)} /></Field>}
        {rec.kind === 'seasonal' && <SeasonsField seasons={rec.seasons || DEFAULT_SEASONS} onChange={(v) => setRec('seasons', v)} />}
        <div style={s.row2}>
          <Field label="Put the job on the board" hint="days before it's due"><input style={s.input} type="number" min="0" max="60" value={form.leadDays} onChange={(e) => set('leadDays', e.target.value)} /></Field>
          <Field label="Starting from" hint="leave empty for today"><input style={s.input} type="date" value={form.startDay || ''} onChange={(e) => set('startDay', e.target.value)} /></Field>
        </div>
        <div style={s.row2}>
          <Field label="Type"><select style={s.select} value={form.type} onChange={(e) => set('type', e.target.value)}>{Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="Technician"><StaffSelect staff={ctx.staff} value={form.assignedTo} onChange={(v) => set('assignedTo', v || '')} /></Field>
        </div>
        <Field label="Checklist for each visit">
          <select style={s.select} value={form.checklistId || ''} onChange={(e) => set('checklistId', e.target.value)}>
            <option value="">None</option>
            {ctx.checklists.filter((t) => t.active !== false).map((t) => <option key={t.templateId} value={t.templateId}>{t.title}</option>)}
          </select>
        </Field>
        <Field label="What to do on each visit"><textarea style={s.textarea} rows={3} value={form.problem || ''} onChange={(e) => set('problem', e.target.value)} /></Field>
        <div style={s.row2}>
          <Field label="Plan fee" hint='as agreed, e.g. "$250 a visit" or "Free for the first year"'><input style={s.input} value={form.planFee || ''} onChange={(e) => set('planFee', e.target.value)} /></Field>
          <Field label="Billing for each visit">
            <select style={s.select} value={form.billing || ''} onChange={(e) => set('billing', e.target.value)}>
              <option value="">Not decided</option>
              {Object.entries(PLAN_BILLING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
        </div>
        <Field label="To check" hint="for the office — clear it once it's sorted"><textarea style={s.textarea} rows={2} value={form.reviewNote || ''} onChange={(e) => set('reviewNote', e.target.value)} /></Field>
        <label style={s.checkRow}><input type="checkbox" checked={form.active !== false} onChange={(e) => set('active', e.target.checked)} /> Active (untick to pause)</label>
      </div>
      {error && <p style={s.err}>{error}</p>}
      <button style={s.primary} type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save schedule'}</button>
    </form>
  );
}

// ── Parts ──
function Parts({ ctx }) {
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState('');
  const list = ctx.parts.filter((p) => !q || [p.name, p.partNumber, p.location].some((v) => String(v || '').toLowerCase().includes(q.toLowerCase())));
  if (editing) return <PartForm part={editing === 'new' ? null : editing} onDone={() => { setEditing(null); ctx.reloadLists(); }} />;
  const low = ctx.parts.filter((p) => p.active !== false && p.minQty && p.onHand <= p.minQty).length;
  return (
    <div>
      <div style={s.titleRow}>
        <h2 style={s.h2}>Parts</h2>
        <button style={s.primarySm} onClick={() => setEditing('new')}>+ Add part</button>
      </div>
      <p style={s.muted}>Parts used on a job come off the count automatically. {low ? <strong style={{ color: '#b3261e' }}>{low} running low.</strong> : null}</p>
      <input style={{ ...s.input, width: '100%', marginBottom: 12 }} placeholder="Search parts…" value={q} onChange={(e) => setQ(e.target.value)} />
      {list.length === 0 ? <div style={s.empty}>{ctx.parts.length ? 'No parts match.' : 'No parts yet. Add the ones you keep on the van or shelf.'}</div> : list.map((p) => {
        const isLow = p.minQty && p.onHand <= p.minQty;
        return (
          <div key={p.partId} style={{ ...s.card, opacity: p.active === false ? 0.55 : 1 }} role="button" tabIndex={0} onClick={() => setEditing(p)}>
            <div style={s.cardTop}>
              <span style={s.cardTitle}>{p.name}</span>
              <span style={{ ...s.when, color: isLow ? '#b3261e' : '#333', fontWeight: 700 }}>{p.onHand} in stock</span>
            </div>
            <div style={s.cardFoot}>
              {p.partNumber && <span>#{p.partNumber}</span>}
              {p.location && <span>{p.location}</span>}
              {p.minQty ? <span>Reorder at {p.minQty}</span> : null}
              {p.unitCost != null && <span>${Number(p.unitCost).toFixed(2)} each</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PartForm({ part, onDone }) {
  const [form, setForm] = useState({ name: '', partNumber: '', location: '', onHand: 0, minQty: 0, unitCost: '', description: '', active: true, ...(part || {}) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  async function save(e) {
    e.preventDefault(); setBusy(true); setError('');
    try { await serviceApi.savePart(part && part.partId, form); onDone(); } catch (err) { setError(err.message); setBusy(false); }
  }
  return (
    <form onSubmit={save}>
      <button type="button" style={s.backBtn} onClick={onDone}>← Back</button>
      <h2 style={s.h2}>{part ? 'Edit part' : 'Add a part'}</h2>
      <div style={s.formGrid}>
        <Field label="Name"><input style={s.input} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Group head gasket 8mm" /></Field>
        <div style={s.row2}>
          <Field label="Part number"><input style={s.input} value={form.partNumber} onChange={(e) => set('partNumber', e.target.value)} /></Field>
          <Field label="Where it's kept"><input style={s.input} value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Van, shelf B…" /></Field>
        </div>
        <div style={s.row2}>
          <Field label="In stock now" hint="change this after a count"><input style={s.input} type="number" value={form.onHand} onChange={(e) => set('onHand', e.target.value)} /></Field>
          <Field label="Reorder when down to"><input style={s.input} type="number" min="0" value={form.minQty} onChange={(e) => set('minQty', e.target.value)} /></Field>
        </div>
        <Field label="Cost each ($)"><input style={s.input} type="number" min="0" step="0.01" value={form.unitCost ?? ''} onChange={(e) => set('unitCost', e.target.value)} /></Field>
        <label style={s.checkRow}><input type="checkbox" checked={form.active !== false} onChange={(e) => set('active', e.target.checked)} /> Still stocked</label>
      </div>
      {error && <p style={s.err}>{error}</p>}
      <button style={s.primary} type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save part'}</button>
    </form>
  );
}

// ── Checklist templates ──
// ── Trends: which customers and machines we keep going back to ──
const TREND_PERIODS = [[3, '3 months'], [6, '6 months'], [12, '12 months'], [24, '2 years'], [0, 'All time']];
// Categorical slots in fixed order (validated for colour-blind separation);
// the legend, tooltip and table carry the names, never colour alone.
const TREND_SERIES = [
  ['repair', 'Repairs', '#2a78d6'], ['maintenance', 'PM & filters', '#eb6834'], ['inspection', 'Inspections', '#1baf7a'],
  ['install', 'Installs', '#eda100'], ['other', 'Other', '#e87ba4'],
];
const monthName = (m, long) => new Date(`${m}-15T12:00:00Z`).toLocaleDateString('en-US', long ? { month: 'long', year: 'numeric' } : { month: 'short' });
const shortDay = (d) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

function Trends({ ctx }) {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [equip, setEquip] = useState('all');
  const [only, setOnly] = useState('all');
  const [sort, setSort] = useState('visits');
  const [open, setOpen] = useState(null);
  useEffect(() => {
    setData(null); setError('');
    serviceApi.trends(months).then(setData).catch((e) => setError(e.message));
  }, [months, ctx.refreshKey]);

  const equipmentKinds = useMemo(() => [...new Set((data ? data.customers : []).flatMap((c) => Object.keys(c.equipment)))].sort(), [data]);
  const customers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (data ? data.customers : []).filter((c) => (!needle || c.name.toLowerCase().includes(needle)
      || c.machines.some((m) => m.label.toLowerCase().includes(needle)))
      && (equip === 'all' || c.equipment[equip] > 0)
      && (only === 'all' || (only === 'comebacks' && c.comebacks > 0) || (only === 'open' && c.open > 0) || (only === 'unmatched' && !c.matched)));
    const by = {
      visits: (a, b) => b.visits - a.visits,
      comebacks: (a, b) => b.comebacks - a.comebacks || b.visits - a.visits,
      last: (a, b) => String(b.lastVisit).localeCompare(String(a.lastVisit)),
      name: (a, b) => a.name.localeCompare(b.name),
    }[sort];
    return [...list].sort(by);
  }, [data, q, equip, only, sort]);
  const filtering = q.trim() || equip !== 'all' || only !== 'all';

  const t = data && data.totals;
  return (
    <div>
      <div style={s.titleRow}><h2 style={s.h2}>Trends</h2></div>
      <div style={s.filters}>
        <div style={s.segment}>
          {TREND_PERIODS.map(([m, label]) => (
            <button key={m} style={months === m ? { ...s.segBtn, ...s.segActive } : s.segBtn} onClick={() => setMonths(m)}>{label}</button>
          ))}
        </div>
      </div>
      {error && <p style={s.err}>{error}</p>}
      {!data && !error && <p style={s.muted}>Working it out…</p>}
      {data && (
        <>
          <div style={s.tiles}>
            <StatTile label="Visits" value={t.visits} sub={`since ${shortDay(data.since)}`} />
            <StatTile label="Customers visited" value={t.customers} />
            <StatTile label="Repairs" value={t.byType.repair} sub={`${t.byType.maintenance} maintenance visits`} />
            <StatTile label={`Back within ${data.comebackDays} days`} value={t.comebacks}
              sub={t.byType.repair ? `${Math.round((t.comebacks / t.byType.repair) * 100)}% of repairs` : null} />
            {data.returnVisits && <StatTile label="Return visits" value={data.returnVisits.total} sub="opened from a completed job" />}
          </div>
          <MonthlyVisitsChart monthly={data.monthly} />
          {data.returnVisits && data.returnVisits.total > 0 && (
            <section style={s.section}>
              <h3 style={s.h3}>Return visits, by whose work had to be redone</h3>
              <div style={s.tableScroll}>
                <table style={s.trTable}>
                  <thead><tr><th style={s.trTh}>Technician on the first visit</th><th style={s.trThR}>Return visits</th></tr></thead>
                  <tbody>{data.returnVisits.byTechnician.map((r) => (
                    <tr key={r.name}><td style={s.trTd}>{r.name}</td><td style={s.trTdR}>{r.count}</td></tr>
                  ))}</tbody>
                </table>
              </div>
              {data.returnVisits.jobs.map((j) => (
                <button key={j.jobId} type="button" style={s.trJob} onClick={() => ctx.openJob(j.jobId)}>
                  <span style={s.trJobNo}>{j.label}</span><span style={s.trJobDay}>{shortDay(j.day)}</span>
                  <span>{j.customerName}</span><span style={s.trJobType}>return visit for {j.of || 'an earlier job'}{j.technician ? ` (${j.technician})` : ''}</span>
                </button>
              ))}
            </section>
          )}
          {data.machines.length > 0 && (
            <section style={s.section}>
              <h3 style={s.h3}>Machines we keep going back to</h3>
              <div style={s.tableScroll}>
                <table style={s.trTable}>
                  <thead><tr><th style={s.trTh}>Machine</th><th style={s.trTh}>Customer</th><th style={s.trThR}>Visits</th><th style={s.trThR}>Back within {data.comebackDays} days</th><th style={s.trThR}>Last visit</th></tr></thead>
                  <tbody>{data.machines.map((m) => (
                    <tr key={`${m.customerKey}|${m.label}`}>
                      <td style={s.trTd}>{m.label}{m.linked ? '' : <span style={s.trNote}> (as typed on jobs)</span>}</td>
                      <td style={s.trTd}>{m.customerName}</td>
                      <td style={s.trTdR}>{m.visits}</td><td style={s.trTdR}>{m.comebacks || '—'}</td><td style={s.trTdR}>{shortDay(m.lastVisit)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <p style={s.muted}>Machines are only as good as what's recorded: pick the machine on each job (or type its model in the checklist) and this list sharpens.</p>
            </section>
          )}
          <section style={s.section}>
            <div style={s.titleRow}>
              <h3 style={{ ...s.h3, margin: 0 }}>By customer</h3>
              <span style={s.count}>{filtering ? `Showing ${customers.length} of ${data.customers.length}` : `${data.customers.length} customers`}</span>
            </div>
            <div style={s.filters}>
              <input style={{ ...s.input, flex: '1 1 220px' }} placeholder="Find a customer or machine…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a customer or machine" />
              <select style={s.select} value={equip} onChange={(e) => setEquip(e.target.value)} aria-label="Equipment">
                <option value="all">All equipment</option>
                {equipmentKinds.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <select style={s.select} value={only} onChange={(e) => setOnly(e.target.value)} aria-label="Show">
                <option value="all">All customers</option>
                <option value="comebacks">Back within {data.comebackDays} days</option>
                <option value="open">With open jobs</option>
                <option value="unmatched">Not matched to SOS</option>
              </select>
              {filtering && <button style={s.outlineSm} onClick={() => { setQ(''); setEquip('all'); setOnly('all'); }}>Clear</button>}
            </div>
            <div style={s.tableScroll}>
              <table style={s.trTable}>
                <thead><tr>
                  <SortTh label="Customer" k="name" sort={sort} setSort={setSort} />
                  <SortTh label="Visits" k="visits" sort={sort} setSort={setSort} right />
                  <th style={s.trThR}>Repairs</th><th style={s.trThR}>PM</th>
                  <SortTh label={`Back ≤${data.comebackDays}d`} k="comebacks" sort={sort} setSort={setSort} right />
                  <th style={s.trThR}>Every</th>
                  <SortTh label="Last visit" k="last" sort={sort} setSort={setSort} right />
                  <th style={s.trTh}>Equipment</th>
                  <th style={s.trTh}>By month</th>
                </tr></thead>
                <tbody>{customers.map((c) => (
                  <CustomerTrendRow key={c.key} c={c} monthly={data.monthly} open={open === c.key} onToggle={() => setOpen(open === c.key ? null : c.key)} onOpenJob={ctx.openJob} comebackDays={data.comebackDays} />
                ))}</tbody>
              </table>
            </div>
            {customers.length === 0 && <div style={s.empty}>No visits match.</div>}
          </section>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, sub }) {
  return (
    <div style={s.tile}>
      <div style={s.tileLabel}>{label}</div>
      <div style={s.tileValue}>{Number(value || 0).toLocaleString('en-US')}</div>
      {sub && <div style={s.tileSub}>{sub}</div>}
    </div>
  );
}

function SortTh({ label, k, sort, setSort, right }) {
  return (
    <th style={right ? s.trThR : s.trTh} aria-sort={sort === k ? 'descending' : 'none'}>
      <button style={{ ...s.sortBtn, fontWeight: sort === k ? 700 : 600 }} onClick={() => setSort(k)}>{label}{sort === k ? ' ↓' : ''}</button>
    </th>
  );
}

function CustomerTrendRow({ c, monthly, open, onToggle, onOpenJob, comebackDays }) {
  const equipment = Object.entries(c.equipment).sort((a, b) => b[1] - a[1]);
  const last12 = c.monthly.slice(-12);
  const months12 = monthly.slice(-12);
  const peak = Math.max(1, ...last12);
  return (
    <>
      <tr style={open ? s.trRowOpen : s.trRow} onClick={onToggle} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onToggle()} aria-expanded={open}>
        <td style={{ ...s.trTd, minWidth: 180 }}><span style={s.trChevron}>{open ? '▾' : '▸'}</span> {c.name}{!c.matched && <span style={s.trNote}> (not matched to SOS)</span>}</td>
        <td style={{ ...s.trTdR, fontWeight: 600 }}>{c.visits}</td>
        <td style={s.trTdR}>{c.byType.repair || '—'}</td>
        <td style={s.trTdR}>{c.byType.maintenance || '—'}</td>
        <td style={s.trTdR}>{c.comebacks || '—'}</td>
        <td style={s.trTdR}>{c.avgDaysBetween ? `${c.avgDaysBetween} d` : '—'}</td>
        <td style={s.trTdR}>{shortDay(c.lastVisit)}</td>
        <td style={s.trTd}>{equipment.length ? equipment.map(([k, n]) => <div key={k} style={{ whiteSpace: 'nowrap' }}>{k} {n}</div>) : '—'}</td>
        <td style={s.trTd}>
          <div style={s.spark} role="img" aria-label={`Visits by month: ${last12.map((n, i) => `${monthName(months12[i].month)} ${n}`).join(', ')}`}>
            {last12.map((n, i) => <span key={i} title={`${monthName(months12[i].month, true)}: ${n}`} style={{ ...s.sparkBar, height: n ? `${Math.max(3, (n / peak) * 20)}px` : '1px', opacity: n ? 1 : 0.35 }} />)}
          </div>
        </td>
      </tr>
      {open && (
        <tr><td colSpan={9} style={s.trDetail}>
          {c.machines.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={s.fieldLabel}>Machines</div>
              <div style={s.trMachines}>{c.machines.map((m) => (
                <span key={m.label} style={s.trMachine}>{m.label} · {m.visits} visit{m.visits === 1 ? '' : 's'}{m.comebacks ? ` · ${m.comebacks} back within ${comebackDays} days` : ''}</span>
              ))}</div>
            </div>
          )}
          <div style={s.fieldLabel}>Jobs</div>
          {c.jobs.map((j) => (
            <button key={j.jobId} style={s.trJob} onClick={() => onOpenJob(j.jobId)}>
              <span style={s.trJobNo}>{j.label || 'Job'}</span>
              <span style={s.trJobDay}>{shortDay(j.day)}</span>
              <span style={s.trJobType}>{TYPE_LABELS[j.type] || j.type}</span>
              {j.comeback && <span style={s.trBack}>Back within {comebackDays} days</span>}
              <span style={s.trJobText}>{j.problem}{j.machine ? ` — ${j.machine}` : ''}</span>
            </button>
          ))}
        </td></tr>
      )}
    </>
  );
}

// Visits per month, stacked by kind of job. Hover or focus a month for its
// numbers; the table view holds every value without hovering.
function MonthlyVisitsChart({ monthly }) {
  const [hover, setHover] = useState(null);
  const [asTable, setAsTable] = useState(false);
  const max = Math.max(1, ...monthly.map((m) => m.total));
  const step = max <= 5 ? 1 : max <= 12 ? 2 : max <= 30 ? 5 : 10;
  const top = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  const H = 180;
  return (
    <section style={s.section}>
      <div style={s.titleRow}>
        <h3 style={{ ...s.h3, margin: 0 }}>Visits by month</h3>
        <button style={s.chartToggle} onClick={() => setAsTable((v) => !v)}>{asTable ? 'Show chart' : 'Show as table'}</button>
      </div>
      <div style={s.legend}>
        {TREND_SERIES.map(([k, label, color]) => <span key={k} style={s.legendItem}><span style={{ ...s.legendSwatch, background: color }} />{label}</span>)}
      </div>
      {asTable ? (
        <div style={s.tableScroll}>
          <table style={s.trTable}>
            <thead><tr><th style={s.trTh}>Month</th>{TREND_SERIES.map(([k, label]) => <th key={k} style={s.trThR}>{label}</th>)}<th style={s.trThR}>Total</th></tr></thead>
            <tbody>{monthly.map((m) => (
              <tr key={m.month}><td style={s.trTd}>{monthName(m.month, true)}</td>{TREND_SERIES.map(([k]) => <td key={k} style={s.trTdR}>{m[k] || '—'}</td>)}<td style={{ ...s.trTdR, fontWeight: 600 }}>{m.total}</td></tr>
            ))}</tbody>
          </table>
        </div>
      ) : (
        <div style={s.chartBox}>
          <div style={s.chartAxis}>{ticks.slice().reverse().map((v) => <span key={v} style={{ ...s.chartTick, bottom: `${(v / top) * H - 6}px` }}>{v}</span>)}</div>
          <div style={{ ...s.chartPlot, height: H }}>
            {ticks.map((v) => <div key={v} style={{ ...s.chartGrid, bottom: `${(v / top) * H}px`, ...(v === 0 ? s.chartBase : {}) }} />)}
            {monthly.map((m, i) => {
              const segs = TREND_SERIES.filter(([k]) => m[k] > 0);
              return (
                <div key={m.month} style={s.chartCol} tabIndex={0} aria-label={`${monthName(m.month, true)}: ${m.total} visits`}
                  onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}>
                  <div style={{ ...s.chartStack, height: `${(m.total / top) * H}px`, opacity: hover === null || hover === i ? 1 : 0.55 }}>
                    {segs.map(([k, , color], si) => (
                      <div key={k} style={{ background: color, flex: m[k], minHeight: 2, borderRadius: si === segs.length - 1 ? '4px 4px 0 0' : 0, marginTop: si === 0 ? 0 : 2 }} />
                    )).reverse()}
                  </div>
                  <span style={s.chartLabel}>{monthName(m.month)}</span>
                  {hover === i && (
                    <div style={{ ...s.chartTip, ...(i > monthly.length / 2 ? { right: '50%' } : { left: '50%' }) }} role="tooltip">
                      <div style={s.chartTipHead}>{monthName(m.month, true)}</div>
                      {TREND_SERIES.map(([k, label, color]) => (
                        <div key={k} style={s.chartTipRow}><span style={{ ...s.chartTipKey, background: color }} /><strong>{m[k]}</strong><span style={s.chartTipName}>{label}</span></div>
                      ))}
                      <div style={{ ...s.chartTipRow, borderTop: '1px solid #eee', paddingTop: 4 }}><strong>{m.total}</strong><span style={s.chartTipName}>visits</span></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

const STEP_KINDS = {
  check: 'Tick box', text: 'Write a note', number: 'Number', yes_no_na: 'Yes / No / N/A', choice: 'Multiple choice',
  photo: 'Photo', signature: 'Signature', heading: 'Section heading',
};
function Checklists({ ctx }) {
  const [editing, setEditing] = useState(null);
  if (editing) return <ChecklistForm template={editing === 'new' ? null : editing} onDone={() => { setEditing(null); ctx.reloadLists(); }} />;
  return (
    <div>
      <div style={s.titleRow}>
        <h2 style={s.h2}>Checklists</h2>
        <button style={s.primarySm} onClick={() => setEditing('new')}>+ New checklist</button>
      </div>
      <p style={s.muted}>Step-by-step procedures — a PM, a trouble-shoot, the customer's signature. Add one to any job, or to a schedule so every visit gets it.</p>
      {ctx.checklists.length === 0 ? <div style={s.empty}>No checklists yet.</div> : ctx.checklists.map((t) => (
        <div key={t.templateId} style={{ ...s.card, opacity: t.active === false ? 0.55 : 1 }} role="button" tabIndex={0} onClick={() => setEditing(t)}>
          <div style={s.cardTitle}>{t.title}</div>
          <div style={s.cardFoot}><span>{t.items.length} step{t.items.length === 1 ? '' : 's'}</span>{t.active === false && <span>Retired</span>}</div>
        </div>
      ))}
    </div>
  );
}

function ChecklistForm({ template, onDone }) {
  const [title, setTitle] = useState(template ? template.title : '');
  const [items, setItems] = useState(template ? template.items.map((x) => ({ ...x })) : [{ label: '', kind: 'check' }]);
  const [active, setActive] = useState(template ? template.active !== false : true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const setItem = (i, k, v) => setItems((list) => list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const move = (i, d) => setItems((list) => { const n = [...list]; const [x] = n.splice(i, 1); n.splice(Math.max(0, Math.min(n.length, i + d)), 0, x); return n; });
  async function save(e) {
    e.preventDefault(); setBusy(true); setError('');
    try { await serviceApi.saveChecklist(template && template.templateId, { title, items, active }); onDone(); } catch (err) { setError(err.message); setBusy(false); }
  }
  return (
    <form onSubmit={save}>
      <button type="button" style={s.backBtn} onClick={onDone}>← Back</button>
      <h2 style={s.h2}>{template ? 'Edit checklist' : 'New checklist'}</h2>
      <div style={s.formGrid}>
        <Field label="Name"><input style={s.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Preventative Maintenance" /></Field>
        <Field label="Steps">
          {items.map((it, i) => (
            <div key={i} style={s.partRow}>
              <input style={{ ...s.input, flex: 1 }} value={it.label} placeholder={`Step ${i + 1}`} onChange={(e) => setItem(i, 'label', e.target.value)} />
              <select style={{ ...s.select, width: 160 }} value={it.kind} onChange={(e) => setItem(i, 'kind', e.target.value)}>{Object.entries(STEP_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
              <button type="button" style={s.xBtn} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
              <button type="button" style={s.xBtn} onClick={() => setItems((list) => list.filter((_, j) => j !== i))} aria-label="Remove step">×</button>
              {it.kind === 'choice' && (
                <input style={{ ...s.input, flexBasis: '100%' }} placeholder="Choices, separated by commas: Espresso, Brewer, Grinder"
                  value={Array.isArray(it.options) ? it.options.join(', ') : (it.options || '')} onChange={(e) => setItem(i, 'options', e.target.value)} />
              )}
              {it.kind === 'text' && (
                <label style={s.stepFlag}><input type="checkbox" checked={isActionStep(it)} onChange={(e) => setItem(i, 'actionItem', e.target.checked)} /> Email the office what's written here (an action item)</label>
              )}
            </div>
          ))}
          <button type="button" style={s.outlineSm} onClick={() => setItems((list) => [...list, { label: '', kind: 'check' }])}>+ Add a step</button>
        </Field>
        <label style={s.checkRow}><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> In use (untick to retire it)</label>
      </div>
      {template && <p style={s.muted}>Changes apply to jobs that get this checklist from now on; jobs that already have it keep their copy.</p>}
      {error && <p style={s.err}>{error}</p>}
      <button style={s.primary} type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save checklist'}</button>
    </form>
  );
}

const s = {

  page: { minHeight: '100vh', background: '#f5f5f5', color: '#333' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 16px', background: RED, color: '#fff', flexWrap: 'wrap' },
  logoImg: { height: 28, display: 'block' },
  brandSmSub: { marginLeft: 10, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: 2, fontSize: '.65rem', whiteSpace: 'nowrap' },
  topRight: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  who: { color: 'rgba(255,255,255,0.9)', fontSize: '.85rem' },
  linkBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', cursor: 'pointer', fontSize: '.8rem', padding: '4px 10px', borderRadius: 6 },
  tabs: { display: 'flex', gap: 4, padding: '0 12px', background: '#fff', borderBottom: '1px solid #e5e5e5', overflowX: 'auto' },
  tab: { padding: '12px 14px', border: 'none', background: 'none', fontSize: '.92rem', color: '#777', cursor: 'pointer', borderBottom: '3px solid transparent', whiteSpace: 'nowrap', fontWeight: 500 },
  tabActive: { color: RED, borderBottom: `3px solid ${RED}`, fontWeight: 600 },
  body: { maxWidth: 860, margin: '0 auto', padding: '16px 16px 48px' },
  bodyWide: { maxWidth: 1280 },

  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, margin: '4px 0 12px' },
  h2: { margin: 0, fontSize: '1.35rem', color: '#222' },
  h3: { margin: '0 0 10px', fontSize: '1.02rem', color: '#333' },
  count: { color: '#999', fontWeight: 500, fontSize: '.85rem', marginLeft: 4 },
  section: { margin: '20px 0' },
  muted: { color: '#888', fontSize: '.86rem', margin: '4px 0' },
  err: { color: '#c0392b', fontSize: '.9rem' },
  empty: { background: '#fff', border: '1px dashed #ddd', borderRadius: 10, padding: '20px 16px', color: '#777', textAlign: 'center', fontSize: '.92rem' },
  notice: { margin: '0 0 12px', padding: '10px 14px', background: '#fff8e6', border: '1px solid #f3dca5', borderRadius: 8, fontSize: '.88rem' },

  card: { background: '#fff', border: '1px solid #e3e3e3', borderLeft: '1px solid #e3e3e3', borderRadius: 10, padding: '12px 14px', marginBottom: 10, cursor: 'pointer' },
  cardReturn: { borderLeft: '4px solid #b3261e' },
  returnFlag: { background: '#fdeeee', color: '#a32020', borderRadius: 999, padding: '2px 9px', fontSize: '.72rem', fontWeight: 600, whiteSpace: 'nowrap' },
  flagRed: { color: '#b3261e', fontWeight: 600 },
  flagGreen: { color: '#1a7f37', fontWeight: 600 },
  cardTop: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  jobNo: { fontWeight: 700, color: '#444', fontSize: '.85rem', fontVariantNumeric: 'tabular-nums' },
  when: { marginLeft: 'auto', fontSize: '.8rem', color: '#777', whiteSpace: 'nowrap' },
  cardTitle: { fontWeight: 600, fontSize: '1rem', color: '#222', marginTop: 6 },
  cardMachine: { color: '#555', fontSize: '.88rem' },
  cardProblem: { color: '#333', fontSize: '.9rem', marginTop: 4, whiteSpace: 'pre-line', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' },
  cardFoot: { display: 'flex', gap: 12, flexWrap: 'wrap', color: '#888', fontSize: '.8rem', marginTop: 8 },
  pill: { display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: '.72rem', fontWeight: 600, whiteSpace: 'nowrap' },

  filters: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  segment: { display: 'inline-flex', border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', background: '#fff' },
  segBtn: { padding: '9px 12px', border: 'none', background: 'none', fontSize: '.86rem', color: '#555', cursor: 'pointer' },
  segActive: { background: '#fff0f0', color: RED, fontWeight: 600 },

  input: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: '1rem', background: '#fff', color: '#333', boxSizing: 'border-box', minWidth: 0 },
  select: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: '1rem', background: '#fff', color: '#333', minWidth: 0 },
  textarea: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: '1rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', resize: 'vertical' },
  formGrid: { display: 'flex', flexDirection: 'column', gap: 12, margin: '12px 0' },
  row3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))', gap: 8 },
  bookBox: { display: 'flex', flexDirection: 'column', gap: 8, background: '#fafafa', border: '1px solid #ececec', borderRadius: 10, padding: 12 },
  periodNav: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  periodLabel: { fontWeight: 600, fontSize: '.92rem', color: '#333', minWidth: 120, textAlign: 'center' },
  stepFlag: { flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', color: '#555' },
  quickAdd: { display: 'flex', flexDirection: 'column', gap: 10, background: '#fafafa', border: '1px dashed #d8d8d8', borderRadius: 10, padding: 12 },
  row2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 12, alignItems: 'end' },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  fieldLabel: { fontSize: '.8rem', fontWeight: 600, color: '#555' },
  hint: { fontSize: '.76rem', color: '#999' },
  checkRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '.92rem', padding: '10px 0' },
  partRow: { display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' },
  xBtn: { border: '1px solid #ddd', background: '#fff', borderRadius: 8, width: 40, fontSize: '1.2rem', cursor: 'pointer', color: '#888' },

  primary: { padding: '12px 18px', background: RED, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '.95rem' },
  primarySm: { padding: '9px 14px', background: RED, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '.88rem', whiteSpace: 'nowrap' },
  outlineSm: { padding: '9px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', color: '#333', fontSize: '.88rem' },
  backBtn: { background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '.92rem', padding: '4px 0', marginBottom: 8 },
  linkish: { background: 'none', border: 'none', color: '#1a5fb4', cursor: 'pointer', padding: 0, fontSize: '.95rem', textAlign: 'left' },

  detailHead: { display: 'flex', justifyContent: 'space-between', gap: 12 },
  contactRow: { display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0 4px' },
  contactBtn: { padding: '10px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, textDecoration: 'none', color: '#1a5fb4', fontWeight: 600, fontSize: '.9rem' },
  statusRow: { display: 'flex', gap: 8, flexWrap: 'wrap', margin: '14px 0' },
  statusBtn: { padding: '11px 16px', background: '#fff', border: `1px solid ${RED}`, color: RED, borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '.92rem' },
  statusDone: { background: '#1a7f37', border: '1px solid #1a7f37', color: '#fff' },
  statusBtnQuiet: { padding: '11px 14px', background: 'none', border: '1px solid #ddd', color: '#777', borderRadius: 8, cursor: 'pointer', fontSize: '.88rem' },
  saveBar: { display: 'flex', alignItems: 'center', gap: 12 },
  off: { opacity: 0.45, cursor: 'default' },
  savedNote: { color: '#1a7f37', fontSize: '.88rem' },
  inlineForm: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 },

  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 },
  photo: { width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 8, border: '1px solid #e3e3e3', display: 'block' },
  photoAdd: { display: 'flex', alignItems: 'center', justifyContent: 'center', aspectRatio: '1 / 1', border: '1px dashed #bbb', borderRadius: 8, color: RED, fontWeight: 600, fontSize: '.85rem', cursor: 'pointer', background: '#fff', textAlign: 'center', padding: 6 },
  note: { background: '#fff', border: '1px solid #ececec', borderRadius: 8, padding: '8px 12px', marginBottom: 8 },
  noteMeta: { color: '#888', fontSize: '.78rem' },
  noteText: { whiteSpace: 'pre-wrap', fontSize: '.92rem' },
  clHeading: { fontWeight: 700, fontSize: '.8rem', textTransform: 'uppercase', letterSpacing: '.04em', color: '#666', padding: '12px 0 4px', borderTop: '1px solid #f0f0f0' },
  segRow: { display: 'flex', gap: 6 },
  seg: { padding: '8px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', color: '#333', fontSize: '.9rem', minWidth: 56 },
  segOn: { background: RED, border: `1px solid ${RED}`, color: '#fff', fontWeight: 600 },
  fileTile: { display: 'flex', alignItems: 'center', justifyContent: 'center', aspectRatio: '1 / 1', borderRadius: 8, border: '1px solid #e3e3e3', background: '#fafafa', color: '#333', fontSize: '.8rem', textDecoration: 'none', textAlign: 'center', padding: 4 },
  signatureImg: { width: '100%', aspectRatio: '2 / 1', objectFit: 'contain', borderRadius: 8, border: '1px solid #e3e3e3', background: '#fff', display: 'block' },
  sigCanvas: { width: '100%', maxWidth: 480, height: 160, border: '1px solid #ccc', borderRadius: 8, background: '#fff', touchAction: 'none' },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 10, marginBottom: 8 },
  tile: { background: '#fff', border: '1px solid #ececec', borderRadius: 10, padding: '12px 14px' },
  tileLabel: { fontSize: '.8rem', color: '#52514e' },
  tileValue: { fontSize: '1.6rem', fontWeight: 600, color: '#0b0b0b', marginTop: 2 },
  tileSub: { fontSize: '.78rem', color: '#898781', marginTop: 2 },
  legend: { display: 'flex', flexWrap: 'wrap', gap: '6px 14px', margin: '8px 0 10px', fontSize: '.82rem', color: '#52514e' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 6 },
  legendSwatch: { width: 10, height: 10, borderRadius: 2, display: 'inline-block' },
  chartToggle: { background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '.84rem', padding: 0 },
  chartBox: { display: 'flex', gap: 6, background: '#fcfcfb', border: '1px solid #ececec', borderRadius: 10, padding: '14px 12px 8px' },
  chartAxis: { position: 'relative', width: 22, flex: '0 0 22px' },
  chartTick: { position: 'absolute', right: 0, fontSize: '.7rem', color: '#898781' },
  chartPlot: { position: 'relative', flex: 1, display: 'flex', alignItems: 'flex-end', gap: 2, marginBottom: 20 },
  chartGrid: { position: 'absolute', left: 0, right: 0, height: 1, background: '#e1e0d9' },
  chartBase: { background: '#c3c2b7' },
  chartCol: { position: 'relative', flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', outline: 'none', cursor: 'default' },
  chartStack: { width: '100%', maxWidth: 24, display: 'flex', flexDirection: 'column', transition: 'opacity .12s' },
  chartLabel: { position: 'absolute', bottom: -18, fontSize: '.68rem', color: '#898781', whiteSpace: 'nowrap' },
  chartTip: { position: 'absolute', bottom: '100%', marginBottom: 6, zIndex: 3, background: '#fff', border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,.12)', padding: '8px 10px', fontSize: '.8rem', whiteSpace: 'nowrap', pointerEvents: 'none' },
  chartTipHead: { fontWeight: 600, color: '#0b0b0b', marginBottom: 4 },
  chartTipRow: { display: 'flex', alignItems: 'center', gap: 6, color: '#0b0b0b', lineHeight: 1.6 },
  chartTipKey: { width: 10, height: 2, borderRadius: 1, display: 'inline-block' },
  chartTipName: { color: '#52514e' },
  tableScroll: { overflowX: 'auto', border: '1px solid #ececec', borderRadius: 10 },
  trTable: { width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '.86rem' },
  trTh: { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e8e8e8', color: '#666', fontSize: '.76rem', fontWeight: 600, whiteSpace: 'nowrap' },
  trThR: { textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e8e8e8', color: '#666', fontSize: '.76rem', fontWeight: 600, whiteSpace: 'nowrap' },
  trTd: { padding: '8px 10px', borderBottom: '1px solid #f2f2f2', color: '#333', verticalAlign: 'middle' },
  trTdR: { padding: '8px 10px', borderBottom: '1px solid #f2f2f2', color: '#333', textAlign: 'right', whiteSpace: 'nowrap' },
  trRow: { cursor: 'pointer' },
  trTdNw: { padding: '8px 10px', borderBottom: '1px solid #f2f2f2', color: '#333', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  trackJob: { fontWeight: 700, color: '#222', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  reviewNote: { marginTop: 6, padding: '6px 10px', background: '#fff8e6', border: '1px solid #f3dca5', borderRadius: 8, fontSize: '.84rem', color: '#6b4e00' },
  seasonRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: '.9rem', color: '#555' },
  gapText: { color: '#a32020', fontWeight: 600, fontSize: '.92rem' },
  gapCell: { background: '#fdecea', color: '#a32020', fontWeight: 600, fontSize: '.8rem', whiteSpace: 'nowrap' },
  gapChips: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  gapChip: { padding: '8px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: 999, cursor: 'pointer', color: '#444', fontSize: '.86rem', display: 'inline-flex', gap: 6, alignItems: 'baseline' },
  gapChipOn: { border: `1px solid ${RED}`, background: '#fff0f0', color: RED, fontWeight: 600 },
  cellBtn: { background: '#fff', border: '1px solid #d6d6d6', borderRadius: 6, padding: '2px 8px', fontSize: '.76rem', color: '#1a5fb4', cursor: 'pointer', marginLeft: 6, fontWeight: 500 },
  linker: { display: 'flex', flexDirection: 'column', gap: 6, cursor: 'default' },
  found: { background: '#fff', border: '1px solid #e3e3e3', borderRadius: 8, padding: '10px 12px', fontSize: '.9rem', maxWidth: 640 },
  trRowOpen: { cursor: 'pointer', background: '#faf8f6' },
  trChevron: { color: '#999', display: 'inline-block', width: 12 },
  trNote: { color: '#999', fontSize: '.76rem' },
  trDetail: { background: '#faf8f6', padding: '10px 14px 14px', borderBottom: '1px solid #ececec' },
  trMachines: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 },
  trMachine: { background: '#fff', border: '1px solid #e3e3e3', borderRadius: 999, padding: '3px 10px', fontSize: '.8rem', color: '#333' },
  trJob: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 10px', width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #ececec', borderRadius: 8, padding: '8px 10px', marginTop: 6, cursor: 'pointer', fontSize: '.84rem', color: '#333' },
  trJobNo: { fontWeight: 700, color: '#222' },
  trJobDay: { color: '#777' },
  trJobType: { color: '#555' },
  trBack: { background: '#fdeeee', color: '#a32020', borderRadius: 999, padding: '1px 8px', fontSize: '.74rem', fontWeight: 600 },
  trJobText: { flexBasis: '100%', color: '#555' },
  sortBtn: { background: 'none', border: 'none', padding: 0, color: '#666', fontSize: '.76rem', cursor: 'pointer', fontFamily: 'inherit' },
  spark: { display: 'flex', alignItems: 'flex-end', gap: 2, height: 22 },
  sparkBar: { width: 5, background: '#2a78d6', borderRadius: '2px 2px 0 0', display: 'inline-block' },
  checklistBox: { background: '#fff', border: '1px solid #ececec', borderRadius: 10, padding: '10px 12px', marginBottom: 10 },
  checklistHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 },
  checkRowItem: { borderTop: '1px solid #f3f3f3', padding: '4px 0' },
};
